<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->
<template>
  <div class="creditnote-main">
    <div class="left-container" ref="leftContainer">
      <Menus :menu="menu" @menuClicked="menuClicked" />
      <div
        v-if="mountedPromisesFulfilledCount < mountedPromises.length"
        class="loading-bar-container"
        :style="loadingBarContainerStyling"
      >
        <div class="loading-bar-text">
          {{ loadingBarText }}
        </div>
        <div class="loading-bar" :style="loadingBarStyling"></div>
      </div>
    </div>
    <div class="right-container">
      <div class="top-section">
        <div class="filter-description">{{ filterText }}</div>
        <ButtonFilter
          class="button-filter-component"
          filterPosition="right"
          buttonText="Change Filter"
          :filterWidth="300"
          :isOpen.sync="mainFilterIsOpen"
          :buttonIsDisabled="mainFilterIsDisabled"
        >
          <div class="filter-popup">
            <BoxContainer
              title="Date"
              :isCheckBox="true"
              style="margin-bottom: 5px"
              :isChecked.sync="filterDateInvList"
            >
              <div class="row-display">
                <DateTimePicker
                  :showTime="false"
                  placeholder="From"
                  :isDisabled="!filterDateInvList"
                  :value.sync="fromInvoiceListingDate"
                  :errorMessage.sync="fromDateErrorMessage"
                />
                <DateTimePicker
                  :showTime="false"
                  placeholder="To"
                  :isDisabled="!filterDateInvList"
                  :value.sync="toInvoiceListingDate"
                  :errorMessage.sync="toDateErrorMessage"
                />
              </div>
            </BoxContainer>
            <BoxContainer
              title="Search"
              :isCheckBox="true"
              style="margin-botton: 5px"
              :isChecked.sync="filterSearchInvList"
            >
              <div>
                <Dropdown
                  ref="searchby"
                  placeholder="Search By"
                  :items="searchFilterItems"
                  itemKey="Search By"
                  :isDisabled="!filterSearchInvList"
                  :isRequired="filterSearchInvList"
                  :selectedObject.sync="searchByValue"
                  :errorMessage.sync="searchByErrorMessage"
                />
                <Dropdown
                  ref="searchcriteria"
                  placeholder="Search Criteria"
                  :items="searchCriteriaItems"
                  itemKey="name"
                  :isDisabled="!filterSearchInvList"
                  :isRequired="filterSearchInvList"
                  :selectedObject.sync="selectedFilterSearchCriteria"
                  :errorMessage.sync="searchCriteriaErrorMessage"
                />
                <Input
                  ref="search"
                  placeholder="Search"
                  :inputValue.sync="searchInputValue"
                  :isDisabled="!filterSearchInvList"
                  :isRequired="filterSearchInvList"
                  :errorMessage.sync="searchInputErrorMessage"
                />
              </div>
            </BoxContainer>
            <button @click="refreshClicked" :disabled="!filterDateInvList && !filterSearchInvList">Refresh </button>
          </div>
        </ButtonFilter>
        <div>Last Refreshed: {{ lastRefreshed }}</div>
      </div>
      <div class="bottom-section">
        <TableBox
          title="Credit Note"
          :dataType="creditNoteDataType"
          :tableData="creditListing"
          :selectedRow.sync="selectedCreditListing"
          :loading="jobTableBoxIsLoading"
        />
      </div>
    </div>
    <PopupEntry
      v-if="popupShow"
      :show.sync="popupShow"
      :title="popupTitle"
      :createdBy="createdBy"
      :updatedBy="updatedBy"
      @save="saveClicked"
      :requiredError="requiredError"
      @requiredClicked="requiredClicked"
      :successMessages="successMessages"
      :errorMessages="errorMessages"
      :canPrint="true"
      @print="popupPrintClicked"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
      style="display: flex; flex-direction: row; justify-content: space-around; overflow: scroll"
    >
      <div class="cn-entryfields">
        <BoxContainer v-if="action != 'N'" title="Summary of Events" :isCollapsible="true" :isCollapsed="isCollapsed">
          <div class="summary-row">
            <div class="summary-block" v-for="(m, i) in summaryItem" :key="i">
              <Tooltip class="milestone-tooltip" :minWidth="250" :tooltipText="m['Label'].join(',')">
                <div></div>
              </Tooltip>
              <div :id="m['Title']" v-if="m['Title'] != 'PrintCount'" class="summary-item"></div>
              <div v-if="m['Title'] == 'PrintCount'" class="summary-item">
                {{ creditNote[0]['PrintCount'] }}
                <img height="20px" :src="m['Src']" />
              </div>
            </div>
          </div>
          <div class="summary-row">
            <div v-for="(l, i) in Object.entries(newLegend)" :key="i" class="legend-items">
              <div class="legend-box" :style="{ backgroundColor: `${l[1]}` }"></div>
              {{ l[0] }}
            </div>
          </div>
        </BoxContainer>
        <BoxContainer class="first-section" style="display: flex; flex-direction: row; margin-top: 12px">
          <div class="left-section">
            <Dropdown
              :errorMessage.sync="creditTypeErr"
              :isRequired="true"
              placeholder="Credit Note Type"
              ref="invoicetypeinput"
              :selectedObject.sync="selectedCreditTypeList"
              :isDisabled="(viewMode && action != 'N') || selectedCreditTypeList != null"
              :items="header['creditTypeList']"
              itemKey="Description"
              @selected="creditTypeIsSelect"
            />
            <InputQuery
              ref="billCustErr"
              :errorMessage.sync="billCustErr"
              placeholder="Billing Customer"
              :isRequired="true"
              :isDisabled="viewMode || newDetailsList.length > 0"
              :items="header['billingCustList']"
              :selectedObject.sync="selectedBillCustomer"
              @selected="billCustSelected"
              itemKey="Customer Code"
              descKey="Customer Name"
            />
            <InputQuery
              ref="reasonCodeErr"
              :errorMessage.sync="reasonCodeErr"
              placeholder="Reason Code"
              :isRequired="false"
              :isDisabled="viewMode"
              :items="reasonCodeList"
              :selectedObject.sync="selectedReasonCode"
              itemKey="Code"
              descKey="Name"
            />
          </div>
          <div class="right-section">
            <div class="right-section-top" style="display: flex; flex-direction: row; justify-content: space-between">
              <DateTimePicker
                :showTime="false"
                style="text-wrap: nowrap; margin-right: 5px"
                placeholder="Date"
                :isDisabled="viewMode"
                :value.sync="header['creditNoteDate']"
                :errorMessage.sync="creditNoteDateErr"
                ref="creditNoteDateErr"
                :isRequired="true"
              />
              <InputQuery
                style="width: fit-content"
                placeholder="Currency"
                :isRequired="true"
                :tableBoxDataType="tableDataType"
                :items="header['currencyList']"
                :isDisabled="viewMode || newDetailsList.length > 0"
                itemKey="Currency Code"
                descKey="Description"
                :selectedObject.sync="headerSelectedCurrency"
                @selected="headerCurrencySelected"
                ref="currErr"
                :errorMessage.sync="currErr"
                :showDesc="false"
                :allowFreeText="false"
              />
              <Input
                ref="exchErr"
                :errorMessage.sync="exchErr"
                style="text-wrap: nowrap; width: fit-content; margin-right: 5px"
                @focusChanged="headerRateFocusChanged"
                placeholder="Exch. Rate"
                :isRequired="true"
                :isDisabled="viewMode || disabledHeaderRate"
                textType="number"
                textAlign="right"
                :inputValue.sync="header['exchRate']"
                validator="positiveDecimal,14,4"
              />
              <Input
                placeholder="Credit Note No"
                style="width: fit-content; margin-left: 5px"
                :isDisabled="true"
                :inputValue.sync="header['creditNoteNo']"
              />
            </div>
            <div class="right-section-bottom" style="width: 100%">
              <BoxContainer
                title="Credit Note Remarks"
                class="box-container-section"
                style="display: flex; width: 100%"
                :isCollapsible="true"
              >
                <div style="display: flex; flex-direction: row; width: 100%">
                  <RemarksBox
                    :text.sync="header['invoiceRem1']"
                    :isDisabled="viewMode"
                    title=""
                    :maxLength="200"
                    style="margin-right: 5px; width: 100%"
                  />
                  <RemarksBox
                    :text.sync="header['invoiceRem2']"
                    :isDisabled="viewMode"
                    title=""
                    :maxLength="200"
                    style="margin-right: 5px; width: 100%"
                  />
                  <RemarksBox
                    :text.sync="header['invoiceRem3']"
                    :isDisabled="viewMode"
                    title=""
                    :maxLength="200"
                    style="margin-right: 5px; width: 100%"
                  />
                </div>
              </BoxContainer>
            </div>
          </div>
        </BoxContainer>
        <div style="height: 100%; margin-top: 10px">
          <Tab
            :preventTabSwitch="true"
            :tabs="tabsItems"
            style="height: 100%"
            :tabIndex.sync="currentTabIndex"
            @tabClicked="tabClicked"
          >
            <!-- Details Tab -->
            <div slot="Details" :style="{ width: '100%', height: '98%', display: 'flex' }">
              <BoxContainer class="table-container" :style="detailsStyling">
                <div class="tabulator-title">
                  <button
                    v-if="!viewMode && newDetailsList.length > 0"
                    style="margin-right: 3px"
                    @click="addEditCharges('fromDetails')"
                    >Settlement</button
                  >
                  <button v-if="!viewMode" @click="openNewDetails('add')">Add Details</button>
                  <button v-if="!viewMode" @click="openNewDetails('delete')">Delete Details</button>
                </div>
                <TableBox
                  title="Details"
                  :dataType="detailsDataType"
                  @buttonClicked="editButtonClicked"
                  :additionalButtons="viewMode ? [] : editButtons"
                  v-if="showInvoiceChargesTable"
                  :selectedRow.sync="selectedNewDetails"
                  :tableData="newDetailsList"
                  :hideColumns="['InvTxnID', 'invTxnID', 'copy', '#', 'SeqNo', 'TxnCode']"
                />
              </BoxContainer>
              <BoxContainer
                v-if="openNewSetDet"
                class="table-container"
                :style="openNewSetDetStyling"
                ref="openNewSetDetStyling"
              >
                <div style="display: flex; justify-content: space-between">
                  <div>{{ addEditChargesTitle }}</div>
                  <div style="display: flex; justify-content: end">
                    <button style="margin-right: 3px" v-if="!viewMode" @click="addEditCharges('details')">{{
                      selectedNewDetails.length === 0 ? 'Add' : 'Edit'
                    }}</button>
                    <button v-if="!viewMode || action == ''" @click="cancelAddNew('details')">Cancel</button>
                  </div>
                </div>
                <div style="padding-bottom: 3px">
                  <InputQuery
                    style="width: 50%"
                    :isDisabled="viewMode"
                    ref="chargesCode"
                    :errorMessage.sync="selectedChargesCodeErr"
                    placeholder="Charges Code"
                    :isRequired="true"
                    :items="chargesCodeList"
                    :selectedObject.sync="selectedChargesCode"
                    itemKey="Charge Code"
                    descKey="Description"
                    @selected="chargesCodeIsSelected"
                  />
                  <div style="display: flex; flex-direction: row">
                    <InputQuery
                      :showDesc="false"
                      :isDisabled="viewMode"
                      placeholder="Inv./DN No"
                      itemKey="Inv No"
                      :items="invDNList"
                      :selectedObject.sync="selectedInvDN"
                      @selected="invDNIsSelected"
                      :errorMessage.sync="invDNError"
                      ref="invDNError"
                    />
                    <InputQuery
                      :showDesc="false"
                      :isDisabled="viewMode"
                      placeholder="Job No"
                      itemKey="Job No"
                      :rowLimitInputValue.sync="rowLimitInputValue"
                      :items="jobnoList"
                      :selectedObject.sync="selectedJobno"
                      :search.sync="freeTextJobNo"
                    />
                    <InputQuery
                      :showDesc="false"
                      :isDisabled="viewMode || consignmentList.length == 0"
                      placeholder="Consignment No"
                      itemKey="Consignment No"
                      :items="consignmentList"
                      :selectedObject.sync="selectedCNNO"
                    />
                    <InputQuery
                      :showDesc="false"
                      :isDisabled="viewMode"
                      placeholder="Cost Code"
                      itemKey="Cost Code"
                      descKey="Cost Desc"
                      :items="costCodeList"
                      :selectedObject.sync="selectedCostCode"
                    />
                  </div>
                  <button v-if="!viewMode" @click="openSubPackage('Inv')">Select items from Inv/DN</button>
                </div>

                <div class="foreign-currency-main">
                  <div class="foreign-currency-controls">
                    <div class="foreign-currency-row">
                      <div class="foreign-currency-column"></div>
                      <div class="foreign-currency-column"></div>
                      <div class="foreign-currency-column" :style="{ textAlign: 'center' }"> MYR Equivalent </div>
                    </div>
                    <BoxContainer background="rgba(225, 255, 255)" class="foreign-currency-box-container">
                      <div class="foreign-currency-row">
                        <div class="foreign-currency-column">
                          <div class="foreign-currency-row"></div>
                          <div class="foreign-currency-row">
                            <InputQuery
                              placeholder="Currency"
                              :isRequired="true"
                              :items="header['currencyList']"
                              :isDisabled="true"
                              :allowFreeText="false"
                              itemKey="Currency Code"
                              descKey="Description"
                              :selectedObject.sync="selectedCreditCurrency"
                              :showDesc="false"
                              :tableBoxDataType="tableDataType"
                            />
                          </div>
                          <div>
                            <div v-if="GSTEnabled" style="display: flex; flex-direction: row">
                              <InputQuery
                                style="width: 300px"
                                :isDisabled="viewMode"
                                placeholder="Tax Code"
                                itemKey="Tax Code"
                                :allowFreeText="false"
                                :items="taxCodeList"
                                descKey="Tax Code Desc"
                                :selectedObject.sync="selectedTaxCode"
                              />
                              <Input
                                placeholder="Tax Rate %"
                                style="width: 50%; margin-left: 4px"
                                :isDisabled="true"
                                textType="number"
                                textAlign="right"
                                :inputValue.sync="taxRate"
                              />
                            </div>
                            <div style="display: flex; flex-direction: row">
                              <InputQuery
                                placeholder="Tax Code"
                                itemKey="Tax Code"
                                :allowFreeText="false"
                                descKey="Tax Code Desc"
                                :showDesc="false"
                                :isDisabled="viewMode"
                                :items="taxCodeList"
                                :selectedObject.sync="selectedsstTaxCode"
                                @selected="taxCodeSelected"
                              />
                              <Input
                                style="width: 150px"
                                :isRequired="true"
                                :inputValue.sync="details['tax']"
                                ref="tax"
                                :isDisabled="viewMode || !SASEnableTaxRate"
                                :errorMessage.sync="taxErr"
                                placeholder="Srv. Tax %"
                                validator="decimal,12,4"
                                textAlign="right"
                              />
                            </div>
                          </div>
                        </div>
                        <div class="foreign-currency-column" style="width: 33.33%">
                          <div class="foreign-currency-row">
                            <Input
                              placeholder="Inv./DN Amt"
                              :isDisabled="true"
                              textType="number"
                              textAlign="right"
                              :inputValue.sync="indFOutstanding"
                            />
                          </div>
                          <div class="foreign-currency-row">
                            <Input
                              :isRequired="true"
                              :inputValue.sync="details['famount']"
                              ref="famount"
                              :isDisabled="viewMode"
                              :errorMessage.sync="famountErr"
                              placeholder="Credit Amount"
                              validator="decimal,12,4"
                              textAlign="right"
                              style="width: 50%; margin-left: 3px"
                            />
                            <Input
                              placeholder="Exch. Rate"
                              style="width: 50%; margin-left: 3px"
                              :isRequired="true"
                              :isDisabled="true"
                              textType="number"
                              textAlign="right"
                              ref="creditExchRateError"
                              :inputValue.sync="creditExchRate"
                              validator="positiveDecimal,,4"
                            />
                          </div>
                          <div>
                            <div v-if="GSTEnabled" style="display: flex; flex-direction: row">
                              <div style="width: 50%; margin-right: 4px"></div>
                              <Input
                                style="width: 50%; margin-left: 3px"
                                :isRequired="true"
                                :inputValue.sync="details['ftaxable_amountgst2']"
                                ref="ftaxable_amountgst2"
                                :isDisabled="viewMode"
                                :errorMessage.sync="ftaxable_amountgst2Err"
                                placeholder="Tax Amt"
                                validator="decimal,12,4"
                                textAlign="right"
                              />
                            </div>
                            <div style="display: flex; flex-direction: row">
                              <Input
                                style="width: 50%; margin-left: 3px"
                                :isRequired="true"
                                :inputValue="ftaxableamt"
                                ref="ftaxableamt"
                                :isDisabled="true"
                                placeholder="Srv. Tax Amt"
                                validator="decimal,12,4"
                                textAlign="right"
                              />
                              <Input
                                style="width: 50%; margin-left: 3px"
                                :isRequired="true"
                                :inputValue="fnetamt"
                                @focusChanged="focusOut($event, 'fnetamt')"
                                ref="fnetamt"
                                :isDisabled="viewMode"
                                :errorMessage.sync="fnetamtErr"
                                placeholder="Net Amount"
                                validator="decimal,12,4"
                                textAlign="right"
                              />
                            </div>
                          </div>
                        </div>
                        <div class="foreign-currency-column">
                          <div class="foreign-currency-row">
                            <Input
                              placeholder="Inv./DN Amt"
                              :isDisabled="true"
                              textType="number"
                              textAlign="right"
                              :inputValue.sync="indOutstanding"
                            />
                          </div>
                          <div class="foreign-currency-row">
                            <Input
                              placeholder="Credit Amount"
                              :isDisabled="true"
                              :inputValue="myrEquivalentCredit"
                              textType="number"
                              textAlign="right"
                            />
                          </div>
                          <div>
                            <div v-if="GSTEnabled" style="display: flex; flex-direction: row">
                              <div style="width: 50%; margin-right: 3px"></div>
                              <Input
                                placeholder="Tax Amt"
                                style="width: 50%; margin-left: 3px"
                                :isDisabled="true"
                                textType="number"
                                textAlign="right"
                                :inputValue.sync="details['taxable_amountgst2']"
                              />
                            </div>
                            <div style="display: flex; flex-direction: row">
                              <Input
                                placeholder="Srv. Tax Amt"
                                style="width: 50%; margin-left: 3px"
                                :isDisabled="true"
                                textType="number"
                                textAlign="right"
                                :inputValue="taxableamt"
                              />
                              <Input
                                style="width: 50%; margin-left: 3px"
                                :inputValue="netamt"
                                ref="netamt"
                                :isDisabled="viewMode"
                                :errorMessage.sync="fnetamtErr"
                                placeholder="Net Amount"
                                validator="decimal,12,4"
                                textAlign="right"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </BoxContainer>
                  </div>
                  <div class="foreign-currency-background">
                    <div class="background-left"></div>
                    <div class="background-middle"></div>
                    <div class="background-right"></div>
                  </div>
                </div>

                <div style="display: flex; flex-direction: row; height: fit-content">
                  <RemarksBox
                    :text.sync="details['detail_remark']"
                    :isDisabled="viewMode"
                    title=""
                    :maxLength="200"
                    style="margin-right: 5px; width: 100%"
                  />
                  <RemarksBox
                    :text.sync="details['detail_remark2']"
                    :isDisabled="viewMode"
                    title=""
                    :maxLength="200"
                    style="margin-right: 5px; width: 100%"
                  />
                  <RemarksBox
                    :text.sync="details['detail_remark3']"
                    :isDisabled="viewMode"
                    title=""
                    :maxLength="200"
                    style="margin-right: 5px; width: 100%"
                  />
                </div>
              </BoxContainer>
              <BoxContainer
                v-if="openSelectFromDN"
                class="table-container"
                :style="openJobStyling"
                ref="openSelectFromDN"
              >
                <div class="tabulator-title" style="display: flex; flex-direction: row; justify-content: space-between">
                  <div style="display: flex; flex-direction: column; justify-content: start"> </div>
                  <div style="display: flex; justify-content: end">
                    <button
                      style="margin-right: 3px"
                      v-if="selectedSubDetails.length < fromInvList.length"
                      @click="selectAll('inv')"
                      >Select All</button
                    >
                    <button style="margin-right: 3px" v-else @click="selectedSubDetails = []">Clear All</button>
                    <button style="margin-right: 3px" v-if="!viewMode" @click="addEditCharges('copy')">Add</button>
                    <button v-if="!viewMode" @click="cancelAddNew('invlist')">Cancel</button>
                  </div>
                </div>
                <div style="height: 100%">
                  <TableBox
                    v-if="hideSubPackageDetails"
                    :dataType="detailsCopyDataType"
                    style="height: 100%"
                    :title="subPackageTitle"
                    :maxSelection="-1"
                    :selectedRow.sync="selectedSubDetails"
                    :tableData="fromInvList"
                    :hideColumns="['invTxnID', '#', 'SeqNo', 'TxnCode']"
                  />
                </div>
                <!-- <div style="width: 100%; text-align: center; justify-content: center; margin: 6px">

                </div> -->
              </BoxContainer>
            </div>
            <!-- Settlement Tab -->
            <div :slot="settlementTitle" :style="{ display: 'flex', height: '95%' }">
              <BoxContainer class="table-container" :style="settlementStyling">
                <div class="tabulator-title">
                  <button v-if="!viewMode" @click="addSettlement('add')">Add Settlement</button>
                  <button v-if="!viewMode" @click="addSettlement('delete')">Delete Settlement</button>
                </div>
                <TableBox
                  title="Settlement"
                  :dataType="settleDataType"
                  @buttonClicked="settlementEditButtonClicked"
                  :additionalButtons="viewMode ? [] : editButtons"
                  v-if="showInvoiceChargesTable || !viewMode"
                  :selectedRow.sync="selectedSettlement"
                  :tableData="settlementList"
                  :hideColumns="['txnID', '#', 'Txnid', 'JobTxnID', 'Copy', 'SeqNo']"
                />
              </BoxContainer>
              <BoxContainer
                v-if="openNewSet"
                class="table-container"
                :style="openNewSetSetStyling"
                style="height: 100%"
                ref="openNewSetSetStyling"
              >
                <div style="display: flex; justify-content: end">
                  <button style="margin-right: 3px" v-if="!viewMode" @click="addEditCharges('settlement')">{{
                    selectedSettlement.length === 0 ? 'Add' : 'Edit'
                  }}</button>
                  <button v-if="!viewMode || action == ''" @click="cancelAddNew('settlement')">Cancel</button>
                </div>
                <div style="display: flex; flex-direction: row; margin-bottom: 6px">
                  <div style="width: 30%; display: flex; flex-direction: row">
                    <InputQuery
                      :showDesc="false"
                      :isRequired="true"
                      :isDisabled="viewMode"
                      :errorMessage.sync="settlementDocNoErr"
                      placeholder="Doc No"
                      itemKey="Offset Doc"
                      :items="settlementDocNoList"
                      @selected="settlementDocNoIsSelected"
                      :selectedObject.sync="selectedSettlementDocNo"
                      ref="settlementDocNoErr"
                    />
                  </div>
                  <button v-if="!viewMode" @click="openSubPackage('Doc')">Select items from Doc No</button>
                </div>

                <div class="foreign-currency-main">
                  <div class="foreign-currency-controls">
                    <div class="foreign-currency-row">
                      <div class="foreign-currency-column"></div>
                      <div class="foreign-currency-column"></div>
                      <div class="foreign-currency-column" :style="{ textAlign: 'center' }"> MYR Equivalent </div>
                    </div>
                    <BoxContainer background="rgba(225, 255, 255)" class="foreign-currency-box-container">
                      <div class="foreign-currency-row">
                        <div class="foreign-currency-column">
                          <div class="foreign-currency-row">
                            <InputQuery
                              placeholder="Currency"
                              :allowFreeText="false"
                              :tableBoxDataType="tableDataType"
                              :items="header['currencyList']"
                              :isDisabled="true"
                              itemKey="Currency Code"
                              descKey="Description"
                              :selectedObject.sync="headerSelectedCurrency"
                              @selected="currencySelected($event)"
                              :showDesc="false"
                            />
                          </div>
                          <div style="height: 30px" class="foreign-currency-row"></div>
                        </div>
                        <div class="foreign-currency-column" style="width: 33.33%">
                          <div class="foreign-currency-row">
                            <Input
                              :inputValue.sync="setFOutstanding"
                              :isDisabled="true"
                              placeholder="Outstanding Amount"
                              textAlign="right"
                              style="width: 50%; margin-left: 3px"
                            />
                            <Input
                              placeholder="Exch. Rate"
                              @focusChanged="settleRateFocusChanged"
                              :isDisabled="true"
                              textType="number"
                              textAlign="right"
                              :inputValue.sync="header['exchRate']"
                            />
                          </div>
                          <div>
                            <div style="display: flex; flex-direction: row">
                              <Input
                                style="width: 50%; margin-left: 3px"
                                :isRequired="true"
                                @focusChanged="foffsetAmtFocusChanged"
                                :inputValue.sync="foffsetAmt"
                                ref="foffsetAmtErr"
                                :isDisabled="viewMode"
                                :errorMessage.sync="foffsetAmtErr"
                                placeholder="Offset Amount"
                                validator="decimal,12,4"
                                textAlign="right"
                              />
                              <div style="width: 50%; margin-right: 4px"></div>
                            </div>
                          </div>
                        </div>
                        <div class="foreign-currency-column">
                          <div class="foreign-currency-row">
                            <Input
                              placeholder="Outstanding Amount"
                              :isDisabled="true"
                              :inputValue.sync="setOutstanding"
                              textType="number"
                              textAlign="right"
                            />
                          </div>
                          <div>
                            <div style="display: flex; flex-direction: row">
                              <Input
                                placeholder="MYR Equivalent Offset Amount"
                                style="margin-left: 3px"
                                :isDisabled="true"
                                textType="number"
                                textAlign="right"
                                :inputValue="offsetAmt"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </BoxContainer>
                  </div>
                  <div class="foreign-currency-background">
                    <div class="background-left"></div>
                    <div class="background-middle"></div>
                    <div class="background-right"></div>
                  </div>
                </div>
                <div style="display: flex; flex-direction: row; width: 100%">
                  <RemarksBox
                    :text.sync="setRem1"
                    :isDisabled="viewMode"
                    title="Remarks"
                    :maxLength="200"
                    style="margin-right: 5px; width: 100%"
                  />
                  <RemarksBox
                    :text.sync="setRem2"
                    :isDisabled="viewMode"
                    title="Remarks"
                    :maxLength="200"
                    style="margin-right: 5px; width: 100%"
                  />
                  <RemarksBox
                    :text.sync="setRem3"
                    :isDisabled="viewMode"
                    title="Remarks"
                    :maxLength="200"
                    style="margin-right: 5px; width: 100%"
                  />
                </div>
              </BoxContainer>
              <BoxContainer
                v-if="openSelectFromDoc"
                class="table-container"
                :style="openDocStyling"
                ref="openSelectFromDoc"
              >
                <div class="tabulator-title" style="display: flex; flex-direction: row; justify-content: space-between">
                  <div style="display: flex; width: 100%; justify-content: end">
                    <button
                      style="margin-right: 3px"
                      v-if="selectedFromDocList.length < fromDocList.length"
                      @click="selectAll"
                      >Select All</button
                    >
                    <button style="margin-right: 3px" v-else @click="selectedFromDocList = []">Clear All</button>
                    <button style="margin-right: 3px" v-if="!viewMode" @click="addEditCharges('copySettle')"
                      >Add</button
                    >
                    <button v-if="!viewMode" @click="cancelAddNew('doclist')">Cancel</button>
                  </div>
                </div>
                <div style="height: 100%">
                  <TableBox
                    v-if="hideSubPackageSettlement"
                    :dataType="settleCopyDataType"
                    style="height: 100%"
                    :title="subPackageTitle"
                    :maxSelection="-1"
                    :selectedRow.sync="selectedFromDocList"
                    :tableData="fromDocList"
                    :hideColumns="['Txnid', '#']"
                  />
                </div>
              </BoxContainer>
            </div>
            <!-- Additional Info tab -->
            <div slot="Additional Info" style="height: 100%; width: 100%">
              <div style="width: 100%">
                <div style="display: flex; flex-direction: row; width: 100%; margin-bottom: 13px">
                  <BoxContainer title="Delivery Details" style="width: 50%; margin-right: 4px" :isCollapsible="true">
                    <Input
                      :inputValue.sync="companyName"
                      validator="text,40"
                      :isDisabled="viewMode"
                      :errorMessage.sync="companyNameErr"
                      placeholder="Company Name"
                    />
                    <div style="display: flex; flex-direction: row">
                      <Input
                        :inputValue.sync="companyTIN"
                        :isDisabled="viewMode"
                        style="margin-right: 3px"
                        :errorMessage.sync="companyTinErr"
                        placeholder="Company TIN"
                        validator="alphanumeric,13,"
                      />
                      <Input
                        :inputValue.sync="companyRegNo"
                        :isDisabled="viewMode"
                        placeholder="Company Reg. No."
                        :errorMessage.sync="companyRegNoErr"
                        validator="alphanumeric,12,"
                      />
                    </div>
                    <AddressFormV2
                      :address.sync="defaultAddressData"
                      :addressErrors.sync="addressErrors"
                      :viewMode="viewMode"
                      :postcodes="postcodeList"
                      :states="stateList"
                      :countries="countryList"
                      ref="addressForm"
                    />
                  </BoxContainer>
                  <BoxContainer
                    title="Report Header Description"
                    :isCheckBox="true"
                    :checkBoxIsDisabled="viewMode || (selectedJobInfo && jobList.length == 1)"
                    @checkBoxClicked="rptHdrChange"
                    :isChecked.sync="reportHdrCheck"
                    style="width: 50%"
                    :isCollapsible="true"
                  >
                    <div v-if="!showInfoFromJob && !showFromTemplate && reportHdrCheck" style="color: red"
                      >Please select any of the Report Header below</div
                    >
                    <div style="display: flex; flex-direction: row; margin: 3px">
                      <RadioButtonSet
                        labelKey="label"
                        :isDisabled="viewMode"
                        name="header"
                        @radioChanged="radioChanged"
                        style="margin-top: 5px"
                        :isHorizontal="true"
                        title=""
                        :selected.sync="selectedHeaderDesc"
                        :buttons="headerDescRadioButtons"
                      />
                    </div>
                    <div style="display: flex; flex-direction: row; margin: 3px" v-if="showInfoFromJob">
                      <InputQuery
                        :isDisabled="viewMode || (selectedJobInfo && jobList.length == 1)"
                        style="width: 400px; margin-top: 6px"
                        placeholder="Job"
                        :items="jobList"
                        itemKey="JobNo"
                        :selectedObject.sync="selectedJobInfo"
                        :showDesc="false"
                      />
                      <Tooltip
                        class="milestone-tooltip"
                        tooltipText="Please make sure any of the Details have Job No"
                      />
                    </div>
                    <div v-if="showFromTemplate" style="display: flex; flex-direction: row; margin: 6px; height: 100%">
                      <RemarksBox
                        :isDisabled="viewMode"
                        style="height: 120px; margin-right: 3px"
                        :freeText="true"
                        title="Left side"
                        :text.sync="leftSideText"
                      />
                      <RemarksBox
                        :isDisabled="viewMode"
                        style="height: 120px"
                        :freeText="true"
                        title="Right side"
                        :text.sync="rightSideText"
                      />
                    </div>
                  </BoxContainer>
                </div>
                <BoxContainer
                  v-if="false"
                  style="display: flex; flex-direction: column; width: 100%; height: 100%"
                  title="Report Job Info"
                  :isCollapsible="true"
                >
                  <div style="display: flex; flex-direction: row">
                    <div style="width: 50%; margin-right: 6px">
                      <Input
                        validator="positiveDecimal,19,4"
                        :inputValue.sync="gweight"
                        type="number"
                        :errorMessage.sync="gweightErrMessage"
                        ref="gweightErrMessage"
                        :isDisabled="viewMode"
                        style="margin-right: 5px"
                        placeholder="GWeight"
                      />
                      <Input
                        validator="positiveDecimal,19,4"
                        :inputValue.sync="measurement"
                        :isDisabled="viewMode"
                        :errorMessage.sync="measurementErrMessage"
                        ref="measurementErrMessage"
                        placeholder="Measurement"
                      />
                    </div>
                    <div style="width: 50%">
                      <RemarksBox
                        :text.sync="commodity"
                        :errorMessage.sync="commodityErr"
                        :isDisabled="viewMode"
                        :maxLength="254"
                        style="height: fit-content"
                        title="Commodity"
                      />
                      <CheckBoxV2
                        :isChecked="updateJob"
                        :isDisabled="viewMode"
                        style="width: fit-content; text-wrap: nowrap; margin-left: 6px"
                        title="Update Job (Gweight, Measurement ONLY)"
                      />
                      <!-- <dir style="height: fit-content; width: 100%; margin: 0; display: content">Select Container that you want to show in invoice report.</dir>
                      <Filters style="height: 90%" title="Container No"></Filters> -->
                    </div>
                  </div>
                </BoxContainer>
              </div>
            </div>
            <!-- Payment tab -->
            <!-- <div slot="Payment">
              <Dropdown :items="paymentModelist" :selectedObject.sync="selectedPaymentlist" :isDisabled="viewMode" style="width: 30%" placeholder="Payment Mode"></Dropdown>
              <Dropdown :items="bankCodeList" :selectedObject.sync="selectedBankCode" :isDisabled="viewMode" style="width: 30%" placeholder="Bank Code"></Dropdown>
              <DateTimePicker :isDisabled="viewMode" :showTime="false" style="text-wrap: nowrap; width: 30%" placeholder="Payment Date" :value.sync="paymentDate"></DateTimePicker>
              <Dropdown :items="paymentRefNoList" :selectedObject.sync="selectedPaymentRefNo" :isDisabled="viewMode" style="width: 30%" placeholder="Payment Ref No"></Dropdown>
            </div> -->
            <!-- Attachment tab -->
            <div slot="Attachment" style="height: 95%; width: 100%; overflow: scroll">
              <div style="display: flex; justify-content: end; margin-bottom: 6px">
                <button v-if="!viewMode" style="margin-right: 6px" @click="attachmentBtnClicked('add')"
                  >Add Attachments</button
                >
                <button
                  v-if="!viewMode || documentsList.Length > 0"
                  style="margin-right: 6px"
                  @click="attachmentBtnClicked('edit')"
                  >Edit Attachments</button
                >
                <button v-if="!viewMode || documentsList.Length > 0" @click="deleteDocument">Del Attachments</button>
              </div>
              <TableBox
                title="Attachment"
                @buttonClicked="attEditButtonClicked"
                :additionalButtons="[{ text: 'View', index: 0, width: 80 }]"
                :tableData="documentsList"
                :dataType="docDataType"
                :selectedRow.sync="selectedDoc"
                :hideColumns="[
                  'Action',
                  'Doc Image',
                  'Sequence No',
                  'Document GUID',
                  'ID',
                  'Doc Ref No',
                  'Job No',
                  'Doc Code',
                  'Doc Type',
                  'Doc Rec Date',
                  'Doc Rec Time',
                  '_attachmentAction',
                  '#',
                ]"
              />
            </div>
            <!-- Events tab -->
            <div style="height: 95%; overflow: scroll" slot="Events">
              <div style="height: 100%">
                <div style="display: flex; flex-direction: row; width: 100%" ref="salesSection">
                  <BoxContainer
                    title="Show Details"
                    style="width: 50%; display: flex; flex-direction: row; margin-right: 3px"
                  >
                    <CheckBoxV2
                      :isChecked.sync="showGLEntry"
                      :isDisabled="false"
                      style="width: fit-content; text-wrap: nowrap; margin-left: 6px"
                      title="GL Entry"
                    />
                    <CheckBoxV2
                      :isChecked.sync="showeInvoice"
                      :isDisabled="false"
                      style="width: fit-content; text-wrap: nowrap; margin-left: 6px"
                      title="eInvoice Submission"
                    />
                  </BoxContainer>
                  <BoxContainer v-if="false" title="IRBM Details" style="width: 50%; margin-right: 3px">
                    <Input :isDisabled="viewMode" placeholder="IRBM Ref No" />
                    <Input :isDisabled="viewMode" placeholder="IRBM Validation Date and Time" />
                    <Input :isDisabled="viewMode" placeholder="IRBM Original Credit Note No" />
                  </BoxContainer>
                  <BoxContainer title="Sales Person" style="width: 50%; display: flex; flex-direction: row">
                    <InputQuery
                      descKey="Sales Team Name"
                      itemKey="Sales Team Code"
                      :items="salesTeamList"
                      :selectedObject="selectedSalesTeamList"
                      :search.sync="freeTextSalesTeam"
                      :isDisabled="true"
                      style="margin-right: 10px"
                      placeholder="Sales Team"
                    />
                    <InputQuery
                      descKey="Sales Person Name"
                      itemKey="Sales Person Code"
                      :items="salesPersonList"
                      :selectedObject="selectedSalesPersonList"
                      :isDisabled="true"
                      placeholder="Sales Person"
                      :search.sync="freeTextSalesPerson"
                    />
                  </BoxContainer>
                </div>
                <div style="height: 85%; width: 100%; display: flex; flex-direction: row">
                  <BoxContainer v-if="showGLEntry" :style="glEntryStyling" title="GL Entry">
                    <TableBox title="" style="height: 100%" :tableData="glEntryList" :dataType="glDataType" />
                    <div style="display: flex; flex-direction: row; justify-content: space-around; margin-top: 6px">
                      <div style="display: flex; flex-direction: row">
                        <div style="align-self: center; width: 100px">TOTAL DEBIT</div>
                        <div class="total-block">{{ updateGLTotalDebit }}</div>
                      </div>
                      <div style="display: flex; flex-direction: row">
                        <div style="align-self: center; width: 100px">TOTAL CREDIT</div>
                        <div class="total-block">{{ updateGLTotalCredit }}</div>
                      </div>
                    </div>
                  </BoxContainer>
                  <BoxContainer v-if="showeInvoice" :style="eInvoiceStyling" title="LHDN Submission">
                    <TableBox title="" style="height: 100%" :tableData="eInvoiceList" :hideColumns="['#']" />
                  </BoxContainer>
                  <!-- <TableBox style="height: max-content; width: 50%; margin-left: 3px; margin-top: 12px;" title="Offset Documents" :tableData="creditNote['Offset']" :hideColumns="['TxnDet']"/> -->
                </div>
              </div>
            </div>
          </Tab>
        </div>
      </div>
      <BoxContainer>
        <div style="display: flex; flex-direction: row; justify-content: flex-end">
          <div style="display: flex; margin-right: 20px">
            <div style="align-content: center; margin-right: 10px; font-size: 1.1em">CREDIT AMOUNT</div>
            <div style="display: flex">
              <div
                class="total-block"
                style="font-size: 1.1em; text-align: right; display: inline; padding-right: 5px"
                >{{
                  multiCurr ? 'N/A' : (
                    `${headerSelectedCurrency ? headerSelectedCurrency['Currency Code'] : 'MYR'} ${ftotalAmt ? ftotalAmt : '0'}`
                  )
                }}</div
              >
              <div
                class="total-block"
                style="
                  background-color: #467cdc;
                  color: white;
                  font-size: 1.1em;
                  font-weight: bold;
                  text-align: right;
                  display: inline;
                  padding-right: 5px;
                "
                >MYR {{ totalAmt ? totalAmt : '0' }}</div
              >
            </div>
          </div>
          <div style="display: flex">
            <div style="align-content: center; margin-right: 10px; font-size: 1.1em">CREDIT BALANCE</div>
            <div style="display: flex">
              <div
                class="total-block"
                style="font-size: 1.1em; text-align: right; display: inline; padding-right: 5px"
                >{{
                  multiCurr ? 'N/A' : (
                    `${headerSelectedCurrency ? headerSelectedCurrency['Currency Code'] : 'MYR'} ${fbalanceAmt ? fbalanceAmt : '0'}`
                  )
                }}</div
              >
              <div
                class="total-block"
                style="
                  background-color: #467cdc;
                  color: white;
                  font-size: 1.1em;
                  font-weight: bold;
                  text-align: right;
                  display: inline;
                  padding-right: 5px;
                "
                >MYR {{ balanceAmt ? balanceAmt : '0' }}</div
              >
            </div>
          </div>
        </div>
      </BoxContainer>
    </PopupEntry>
    <PopupV2
      :show="showDeleteInvoicePopupWithRemarks"
      title="Delete Credit Note"
      @buttonClicked="deletePopupClicked"
      :buttons="deletePopupButtons"
    >
      <div
        >Please state reason to delete
        {{ selectedCreditListing.length > 0 ? selectedCreditListing[0]['Credit No'] : '' }}?</div
      >
      <Input
        placeholder="Delete Remarks"
        :inputValue.sync="deleteInvoiceRemarks"
        :errorMessage.sync="deleteInvoiceErr"
        :isRequired="true"
      />
    </PopupV2>
    <div class="attachments-popup-container">
      <PopupV2
        class="attachments-popup"
        :show="showAttachmentsPopup"
        :title="attachmentsPopupTitle"
        :buttons="attachmentsPopupButtons"
        @buttonClicked="addDocument"
      >
        <div class="attachments-popup-content">
          <Input
            textType="file"
            placeholder="Select File"
            ref="docFile"
            :isDisabled="viewMode"
            :inputValue.sync="documents['docName']"
            @fileChanged="handleFileUpload"
          />
          <div v-if="docEditMode">
            <div> Attachment: {{ documents['docName'] }} </div>
          </div>
          <div v-if="attachmentsAction == 'edit'" class="attached-file">
            <strong>Attached file: </strong>
            <span>
              {{ selectedDoc.length > 0 ? selectedDoc[0]['File Name'] : '' }}
            </span>
          </div>
          <RemarksBox
            :maxLength="200"
            style="height: 50%"
            title="Remarks"
            :isDisabled="viewMode"
            :text.sync="documents['remarks']"
          />
        </div>
      </PopupV2>
    </div>
    <div v-if="showAttachmentProjector" class="attachment-projector">
      <div class="attachment-projection">
        <object
          v-if="isProjectable"
          :data="attachmentProjectorData"
          :type="attachmentProjectorType"
          draggable="false"
        ></object>
        <div v-else class="attachment-not-projectable-container">
          <span>
            {{ selectedDoc[0]['File Type'].replace('.', '').toUpperCase() }} files cannot be previewed.<br />
            Please download the file and view it instead.
          </span>
          <button @click="downloadAttachment">
            <img src="https://dsmlib.logistics.systems/Icons/Download.png" alt="Download icon" />
            Download
          </button>
        </div>
      </div>
      <div class="close-button" @click="closeProjector"> &times; </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  components: {},
  computed: {
    // Styling for GL Entry in Events tab
    glEntryStyling: function () {
      return {
        height: 'maxContent',
        width: this.showDetailsChecking,
        display: 'flex',
        marginRight: '3px',
        marginTop: '12px',
        flexDirection: 'column',
      };
    },
    // Styling for eInvoice in Events tab
    eInvoiceStyling: function () {
      return {
        height: 'maxContent',
        width: this.showDetailsChecking,
        display: 'flex',
        marginRight: '3px',
        marginTop: '12px',
        flexDirection: 'column',
      };
    },
    // events tab width adjustment according to checkbox selections
    showDetailsChecking: function () {
      if (this.showGLEntry && this.showeInvoice) {
        return '50%';
      } else if (this.showGLEntry || this.showeInvoice) {
        return '100%';
      } else {
        return '0%';
      }
    },
    // Loading bar container styling
    loadingBarContainerStyling: function () {
      return {
        width: `${this.leftMenuWidth - 10}px`,
        fontSize: this.leftMenuWidth < 100 ? '0.8em' : '1em',
      };
    },
    // Populate loading bar text
    loadingBarText: function () {
      const loadingText = this.leftMenuWidth < 100 ? '' : 'Loading ';
      const percentage = ((this.mountedPromisesFulfilledCount / (this.mountedPromises.length || 1)) * 100).toFixed(0);
      // (this.mountedPromises.length || 1) to prevent division by 0,
      // if length is 0, divide by 1 instead. 0/1 is still 0.

      return `${loadingText}${percentage}%`;
    },
    // Width of green progress bar when loading
    loadingBarStyling: function () {
      return {
        width: `${(this.mountedPromisesFulfilledCount / (this.mountedPromises.length || 1)) * 100}%`,
        // (this.mountedPromises.length || 1) to prevent division by 0,
        // if length is 0, divide by 1 instead. 0/1 is still 0.
      };
    },
    // Conditions on disabling header exchange rate
    disabledHeaderRate: function () {
      if (!this.headerSelectedCurrency) {
        return false;
      } else {
        return (
          this.headerSelectedCurrency['Currency Code'] == 'MYR' &&
          this.header['exchRate'] == Number(this.headerSelectedCurrency['Currency Rate']).toFixed(4)
        );
      }
    },
    // Calculate GL Debit amount
    updateGLTotalDebit: function () {
      let glTotalDebit = 0.0;
      if (this.glEntryList.length > 0) {
        this.glEntryList.forEach((gld) => {
          if (gld['Debit Ac'] != '0') {
            glTotalDebit = parseFloat(glTotalDebit) + parseFloat(gld['Amount']);
          }
        });
      }
      return this.$currencyFormat(glTotalDebit);
    },
    // Calculate GL Credit Amount
    updateGLTotalCredit: function () {
      let glTotalCredit = 0.0;
      if (this.glEntryList.length > 0) {
        this.glEntryList.forEach((gld) => {
          if (gld['Credit Ac'] != '0') {
            glTotalCredit = parseFloat(glTotalCredit) + parseFloat(gld['Amount']);
          }
        });
      }
      return this.$currencyFormat(glTotalCredit);
    },
    // Settlement tab title (settlement amount)
    settlementTitle: function () {
      if (this.settlementList.length > 0) {
        let temp = 0;
        temp = this.settlementList
          .map((data) => {
            return Number(data['Offset FAmount']);
          })
          .reduce((a, b) => {
            return a + b;
          });
        this.outstandingAmt = temp.toFixed(2);
        this.tabsItems[1]['title'] = `Settlement (${this.$currencyFormat(temp)})`;
        return `Settlement (${this.$currencyFormat(temp)})`;
      } else {
        this.tabsItems[1]['title'] = 'Settlement (0.00)';
        return 'Settlement (0.00)';
      }
    },
    // Calcaulte foreign tax amount
    ftaxableamt: function () {
      if (this.details['tax'] && this.details['famount']) {
        let temp = (Number(this.details['tax']) / 100) * Number(this.details['famount']);
        return temp.toFixed(2);
      } else {
        return '0.00';
      }
    },
    // fnetamt: function (){
    //   if(this.details['famount']){
    //     return (Number(this.details['famount']) + Number(this.ftaxableamt)).toString()
    //   }else{
    //     return '0.00';
    //   }
    // },
    // Calculate local tax amount
    taxableamt: function () {
      if (this.details['tax']) {
        let taxLocal = (Number(this.details['tax']) / 100) * Number(this.myrEquivalentCredit);
        return taxLocal.toFixed(2);
      } else {
        return '0.00';
      }
    },
    // Calculate net amount
    netamt: function () {
      if (this.myrEquivalentCredit != '0.00') {
        let val = (Number(this.myrEquivalentCredit) + Number(this.taxableamt)).toFixed(2);
        return val.toString();
      } else {
        return '0.00';
      }
    },
    // Calculate offset amount
    offsetAmt: function () {
      if (this.foffsetAmt) {
        return ((Number(this.foffsetAmt) * Number(this.header['exchRate'])) / Number(this.creditFactor)).toFixed(2);
      } else {
        return '0.00';
      }
    },
    // Calculate myr credit amount
    myrEquivalentCredit: function () {
      if (this.details['famount'] && this.creditExchRate) {
        let val = (Number(this.details['famount']) + Number(this.ftaxableamt)).toFixed(2);
        this.fnetamt = val.toString();
        let myrEquivalent = (
          (Number(this.details['famount']) * Number(this.creditExchRate)) /
          Number(this.creditFactor)
        ).toFixed(2);
        this.details['amount'] = myrEquivalent;
        let creditAmt = Number(this.details['amount']).toFixed(2);
        return myrEquivalent != creditAmt && this.selectedCreditCurrency['Currency Code'] != 'MYR' ?
            myrEquivalent
          : creditAmt;
      } else {
        return '0.00';
      }
    },
    // Styling when open new details input fields
    openNewSetDetStyling: function () {
      return {
        width: this.openNewSetDet ? '50%' : '0%',
        height: '100%',
      };
    },
    // Styling when open new settlement input fields
    openNewSetSetStyling: function () {
      return {
        width: this.openNewSet ? '50%' : '0%',
        overflow: 'scroll',
        height: '100%',
      };
    },
    // Styling when select from inv/dn
    openJobStyling: function () {
      return {
        width: this.openSelectFromDN ? '50%' : '0%',
        height: '100%',
        overflow: 'scroll',
      };
    },
    // Styling when select from doc (settlement tab)
    openDocStyling: function () {
      return {
        width: this.openSelectFromDoc ? '50%' : '0%',
        height: '100%',
        overflow: 'scroll',
      };
    },
    // Styling of details table section
    detailsStyling: function () {
      return {
        width: this.openNewSetDet || this.openSelectFromDN ? '50%' : '100%',
        marginRight: '3px',
        height: '100%',
      };
    },
    // Styling of settlement table section
    settlementStyling: function () {
      return {
        width: this.openNewSet || this.openSelectFromDoc ? '50%' : '100%',
        marginRight: '3px',
        height: '100%',
      };
    },
  },
  data: function () {
    return {
      // #region Main
      mountedPromises: [],
      mountedPromisesFulfilledCount: 0,
      mainInvoiceChargesSecHeight: 0,
      menu: [
        {
          title: 'New Credit Note',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-add-new-50.png',
          enabled: true,
          key: 'mnuNew',
        },
        {
          title: 'Edit Credit Note',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-edit-64%20(1).png',
          enabled: true,
          key: 'mnuEdit',
        },
        {
          title: 'Delete Credit Note',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-reduce-50.png',
          enabled: true,
          key: 'mnuDelete',
        },
        {
          title: 'View Credit Note',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-view-64.png',
          enabled: true,
          key: 'mnuView',
        },
        {
          title: 'Print Credit Note',
          icon: 'https://dsmlib.logistics.systems/Icons/Print.png',
          enabled: true,
          key: 'mnuBatch',
        },
        {
          title: 'Reinstate Credit Note',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-loop-50.png',
          enabled: true,
          key: 'mnuReinstateCreditNote',
        },
        {
          title: 'Hide Cancelled Charges',
          icon: 'https://dsmlib.logistics.systems/Icons/hide-file.png',
          enabled: true,
        },
      ],
      currentTabIndex: 0,
      tabsItems: [
        { title: 'Details', disabled: false },
        { title: 'Settlement', disabled: false },
        { title: 'Additional Info', disabled: false },
        // { title: 'Payment', disabled: false }, // hide first
        { title: 'Attachment', disabled: false },
        { title: 'Events', disabled: false }, // hide IRBM section
      ],
      resizeObserver: null,
      leftMenuWidth: 200,
      GSTEnabled: false,
      lastRefreshed: '',
      reasonCodeErr: '',
      jobData: [],
      selectedJob: [],
      jobTableBoxIsLoading: false,
      createdBy: '',
      updatedBy: '',
      popupTitle: '',
      totalAmt: '0.00',
      ftotalAmt: '0.00',
      balanceAmt: '0.00',
      fbalanceAmt: '0.00',
      outstandingAmt: '0.00',
      totalInWords: '',
      oldRate: '',
      popupShow: false,
      requiredError: [],
      successMessages: [],
      errorMessages: [],
      viewMode: false,
      isCollapsed: false,
      filterDate: false,
      filterSearch: false,
      mainFilterIsDisabled: false,
      filterIsDisabled: false,
      postcodeList: [],
      stateList: [],
      countryList: [],
      summaryItem: [
        {
          Title: 'Indicator',
          Label: ['New', 'Cancelled', 'Printed', 'Month End'],
          Indicator: [
            { code: 'N', name: 'New Credit', bg: '#79d279' },
            { code: 'C', name: 'Cancelled', bg: 'FFFFFF' },
            { code: 'P', name: 'Printed', bg: '#79d279' },
            { code: 'M', name: 'Month End', bg: 'FFFFFF' },
          ],
        },
        {
          Title: 'Authorized',
          Label: ['Authorized', 'Unauthorize'],
          Indicator: [
            { code: 'A', name: 'Authorized', bg: '#79d279' },
            { code: 'U', name: 'Unauthorize', bg: '#ffff99' },
          ],
        },
        {
          Title: 'LHDN Submission',
          Label: ['LHDN Queue', 'LHDN Sent', 'LHDN Accepted', 'LHDN Rejected', 'LHDN Cancelled', 'LHDN Pending'],
          Indicator: [
            { code: 'Q', name: 'LHDN Queue', bg: '#ffff99' },
            { code: 'S', name: 'LHDN Sent', bg: '#ffff99' },
            { code: 'A', name: 'LHDN Accepted', bg: '#79d279' },
            { code: 'R', name: 'LHDN Rejected', bg: '#ff8787' },
            { code: 'C', name: 'LHDN Cancelled', bg: 'FFFFFF' },
            { code: 'P', name: 'LHDN Pending', bg: '#ffff99' },
          ],
        },
        {
          Title: 'PrintCount',
          Src: 'https://dsmlib.logistics.systems/Icons/Print.png',
          Label: ['Print Count'],
          Indicator: [{ code: '0', name: 'Print Count' }],
        },
      ],
      errorTabMapping: [
        { key: 'companyNameErr', tab: 1 },
        { key: 'commodityErr', tab: 1 },
        { key: 'companyTinErr', tab: 1 },
        { key: 'companyRegNoErr', tab: 1 },
        { key: 'gweightErrMessage', tab: 1 },
        { key: 'measurementErrMessage', tab: 1 },
      ],
      newLegend: {
        Completed: '#79d279',
        Pending: '#ffff99',
      },
      showDeleteInvoicePopupWithRemarks: false,
      deleteInvoiceRemarks: '',
      deleteInvoiceErr: '',
      deletePopupButtons: [
        { text: 'Delete', color: 'indianred', textColor: 'white' },
        { text: 'Cancel', color: 'lightgray', textColor: 'black' },
      ],
      openSelectFromDN: false,
      chargesFreetext: '',
      openSelectFromDoc: false,
      openNewSetDet: false,
      openNewSet: false,
      selectedCreditTypeList: null,
      creditTypeErr: '',
      currErr: '',
      billCustErr: '',
      exchErr: '',
      headerSelectedCurrency: null,
      detailsSelectedCurrency: null,
      creditNote: [],
      settlementDocNoList: [],
      selectedSettlementDocNo: null,
      settlementDocNoErr: null,
      setFOutstanding: '0.00',
      setOutstanding: '0.00',
      foffsetAmt: '0.00',
      foffsetAmtErr: '',
      selectedBillCustomer: null,
      selectedReasonCode: null,
      internalCustomer: '',
      tableDataType: {
        // available data type string, number, date, currency
        '#': 'number',
        'Currency Code': 'string',
        Description: 'string',
        'Currency Rate': 'currency',
        'Exchange Date': 'date',
        Factor: 'number',
      },
      detailsDataType: {
        // available data type string, number, date, currency
        '#': 'number',
        Tax: 'number',
        DateUpdated: 'date',
        'Tax FAmount': 'currency',
        'Tax Amount': 'currency',
        'Tax Rate': 'currency',
        Rate: 'currency',
        'Net FAmount': 'currency',
        'Srv Tax FAmount': 'currency',
        'Credit FAmount': 'currency',
        'Net Amount': 'currency',
        'Srv Tax Amount': 'currency',
        'Credit Amount': 'currency',
      },
      detailsCopyDataType: {
        // available data type string, number, date, currency
        'fAmt Excl Tax': 'currency',
        'fTax Amt': 'currency',
        DateUpdated: 'date',
        'Amt Excl Tax': 'currency',
        'Tax Amt': 'currency',
        'Amt Incl Tax': 'currency',
        'fAmt Incl Tax': 'currency',
        'Tax Rate': 'number',
        '#': 'number',
      },
      settleDataType: {
        '#': 'number',
        'Offset Amount': 'currency',
        'Offset FAmount': 'currency',
        Rate: 'currency',
        DateUpdated: 'date',
      },
      settleCopyDataType: {
        '#': 'number',
        'Offset Amount': 'currency',
        'Offset FAmount': 'currency',
        Rate: 'currency',
        Outstanding: 'currency',
        'F. Outstanding': 'currency',
        'Original Amount': 'currency',
        Date: 'date',
      },
      glDataType: {
        Amount: 'currency',
      },
      creditNoteDataType: {
        Total: 'currency',
        Balance: 'currency',
        FTotal: 'currency',
        Date: 'date',
      },
      eInvoiceList: [],
      showGLEntry: true,
      showeInvoice: false,
      addressErrors: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        addressLine4: '',
        postcode: '',
        city: '',
        state: '',
        country: '',
      },
      header: {
        currencyList: [],
        billingCustList: [],
        creditTypeList: [],
        billingCust: '',
        selectedCreditType: null,
        creditTypeStr: '',
        creditTerm: '',
        creditNoteDate: new Date(),
        creditNoteNo: '',
        creditAmt: '0.00',
        localAmt: '',
        invoiceRem1: '',
        invoiceRem2: '',
        invoiceRem3: '',
        exchRate: '',
        currFactor: '',
      },
      jobtypeid: '',
      creditNoteDateErr: '',
      multiCurr: false,

      SASEnableTaxRate: true,
      // #endregion

      // #region Credit Note data
      rowLimitInputValue: '1000',
      freeTextJobNo: '',
      freeTextInvNo: '',
      creditFactor: '',
      indFOutstanding: '0.00',
      indOutstanding: '0.00',
      creditExchRate: '',
      selectedCreditCurrency: null,
      creditCurrencyError: '',
      invDNError: '',
      details: {
        famount: '0.00',
        amount: '0.00',
        tax: '0',
        gst2taxrate: '0.00',
        ftaxable_amountgst2: '',
        taxable_amountgst2: '',
        detail_remark: '',
        detail_remark2: '',
        detail_remark3: '',
      },
      userid: '',
      setftaxableamt: '0.00',
      settaxableamt: '0.00',
      deletedJobChargesList: [],
      showInvoiceChargesTable: true,
      editButtons: [{ text: 'Edit', index: 0, width: 60 }],
      oldCurrency: null,
      payableChecking: '',
      selectedSubDetails: [],
      newDetailsList: [],
      settlementList: [],
      deletedSettlementList: [],
      fromInvList: [],
      allFromInvList: [],
      allFromSettlementOffset: [],
      fromDocList: [],
      selectedFromDocList: [],
      deletednewDetailsList: [],
      selectedNewDetails: [],
      selectedSettlement: [],
      jobChargesData: [],
      chargesCodeList: [],
      selectedChargesCode: null,
      selectedChargesCodeErr: '',
      reasonCodeList: [],
      selectedTaxCode: null,
      jobnoList: [],
      selectedJobno: null,
      contList: [],
      invDNList: [],
      selectedInvDN: null,
      selectedcontno: null,
      invoiceDesc: '',
      costCodeList: [],
      consignmentList: [],
      selectedCNNO: null,
      selectedCostCode: null,
      fromDateErrorMessage: '',
      toDateErrorMessage: '',
      creditListing: [],
      selectedCreditListing: [],
      fromInvoiceListingDate: new Date(),
      fromInvoiceListingDateErr: '',
      toInvoiceListingDate: new Date(),
      toInvoiceListingDateErr: '',
      filterDateInvList: true,
      filterSearchInvList: false,
      searchByValue: null,
      searchByErrorMessage: '',
      subPackageTitle: 'Select from ',
      searchFilterItems: [{ 'Search By': 'Credit No', key: 'CN' }],
      searchCriteriaItems: [{ name: 'EQUALS' }, { name: 'STARTS WITH' }, { name: 'CONTAINS' }, { name: 'ENDS WITH' }],
      adjustmentType: [
        { '#': 1, Type: 'Adjustment', Code: 'A' },
        { '#': 2, Type: 'Rebate', Code: 'D' },
      ],
      selectedAdjustment: null,
      selectedFilterSearchCriteria: null,
      searchCriteriaErrorMessage: '',
      searchInputValue: '',
      searchInputErrorMessage: '',
      filterText: '',
      action: '',
      mainFilterIsOpen: false,
      filterIsOpen: false,
      chargesDetailsIndex: -1,
      selectedFilterCngShprConfirm: [],
      selectedFilterCngShprConfirmStr: '',
      selectedFilterJobNoConfirm: [],
      selectedFilterJobNoConfirmStr: '',
      selectedFilterAreaConfirm: [],
      selectedFilterAreaConfirmStr: '',
      selectedFilterSendToConfirm: [],
      selectedFilterSendToConfirmStr: '',
      sendToDataList: [],
      hideSubPackageDetails: true,
      hideSubPackageSettlement: true,
      famountErr: '',
      taxErr: '',
      gst2taxrateErr: '',
      ftaxable_amountgst2Err: '',
      fnetamtErr: '',
      setRem1: '',
      setRem2: '',
      setRem3: '',
      creditError: '',
      creditExchRateError: '',
      netInclTaxError: '',
      // #endregion

      //#region Tax
      taxCodeList: [],
      taxRate: '',
      taxAmt: '',
      ftaxAmt: '',

      sstTaxCodeList: [],
      selectedsstTaxCode: null,
      srvTaxRate: '0',
      fsrvTaxRate: '',
      netInclTax: '', //MYR equivalent
      fnetInclTax: '',
      srvTaxAmt: '',
      fsrvTaxAmt: '',
      fnetamt: '0.00',
      //#endregion
      // #region Addtional data
      selectedHeaderDesc: null,
      companyNameErr: '',
      commodityErr: '',
      companyTinErr: '',
      companyRegNoErr: '',
      creditTermErr: '',
      companyList: [],
      companyName: '',
      companyTIN: '',
      companyRegNo: '',
      defaultAddressData: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        addressLine4: '',
        postcode: '',
        city: '',
        state: '',
        country: '',
      },
      reportHdrCheck: false,
      showInfoFromJob: false,
      selectedJobInfo: null,
      showFromTemplate: false,
      jobList: [],
      gweight: '',
      measurement: '',
      commodity: '',
      updateJob: false,
      selectedContainerNoInfolist: [],
      leftSideTextDefault: 'RMawb/OBL No.\t: \nHawb/HBL No.\t: \nFit/Vsl/Date\t: \nShipper\t\t: \nConsignee\t: ',
      leftSideText: 'RMawb/OBL No.\t: \nHawb/HBL No.\t: \nFit/Vsl/Date\t: \nShipper\t\t: \nConsignee\t: ',
      rightSideTextDefault: 'Origin/Dest\t: \nWeight/CBM\t: \nCommodity\t: \nRemarks\t\t: ',
      rightSideText: 'Origin/Dest\t: \nWeight/CBM\t: \nCommodity\t: \nRemarks\t\t: ',
      gweightErrMessage: '',
      measurementErrMessage: '',
      headerDescRadioButtons: [
        { label: 'Show info from Job', keyValue: '1' },
        { label: 'Show info from template', keyValue: '0' },
      ],
      // #endregion

      // #region Payment data
      paymentModelist: [],
      selectedPaymentlist: null,
      bankCodeList: [],
      selectedBankCode: null,
      paymentDate: new Date(),
      paymentRefNoList: [],
      selectedPaymentRefNo: null,
      // #endregion

      // #region Attachment data
      docEditMode: false,
      docEditButtons: [{ text: 'Edit', index: 0, width: 60 }],
      showDocDetails: false,
      docEditIndex: -1,
      documentsList: [],
      oldDoc: '',
      documents: {
        docName: '',
        attachmentFile: [],
        filetype: '',
        docGuid: '',
        remarks: '',
        docID: 0,
      },
      deletedDocumentsList: [],
      selectedDoc: [],
      addDocuments: false,
      editDocumentIndex: '-1',
      documentsLoading: false,
      attachmentsAction: '', // "Add" | "Edit" | "View"
      attachmentsPopupTitle: '',
      attachmentsPopupButtons: [],
      showAttachmentsPopup: false,
      isProjectable: false,
      showAttachmentProjector: false,
      newAtt: false,
      docDataType: {
        'Doc Date': 'date',
      },
      // #endregion

      //#region attachment projector data fields
      attachmentProjectorFileName: '',
      attachmentProjectorType: '',
      attachmentProjectorData: '',
      //#endregion

      // #region Events data
      salesPersonSection: 0,
      salesPersonList: [],
      selectedSalesPersonList: null,
      salesTeamList: [],
      selectedSalesTeamList: null,
      glEntryList: [],
      offsetDocList: [],
      totalDebit: '',
      totalCredit: '',
      freeTextSalesTeam: '',
      freeTextSalesPerson: '',
      // #endregion
    };
  },
  props: {},
  methods: {
    leftContainerResized: function (resizeObserverEntry) {
      this.leftMenuWidth = resizeObserverEntry[0]['contentRect']['width'];
    },
    menuClicked: async function (e) {
      this.mainFilterIsOpen = false;
      this.clearErrMessage();
      this.clearAllData();
      switch (e['title']) {
        case 'New Credit Note':
          this.action = 'N';
          this.viewMode = true;
          this.filterDate = true;
          this.filterSearch = false;
          this.openSelectFromDN = false;
          this.openSelectFromDoc = false;
          this.editButtons = [{ text: 'Edit', index: 0, width: 60 }];
          this.openNewSetDet = false;
          this.openNewSet = false;
          this.header['creditNoteDate'] = new Date();
          this.header['creditNoteNo'] = '**********';
          this.leftSideText = this.leftSideTextDefault;
          this.rightSideText = this.rightSideTextDefault;
          this.selectedBillCustomer = null;
          this.selectedReasonCode = null;
          this.headerSelectedCurrency = this.header['currencyList'].getSelected('MYR', 'Currency Code');
          this.selectedCreditCurrency = this.header['currencyList'].getSelected('MYR', 'Currency Code');

          this.$nextTick(async () => {
            this.currencySelected(this.headerSelectedCurrency);
            if (this.header['creditTypeList'].length == 1) {
              this.selectedCreditTypeList = this.header['creditTypeList'][0];
              await this.creditTypeIsSelect(this.selectedCreditTypeList);
              this.viewMode = false;
            } else {
              this.$refs['invoicetypeinput'].setFocus();
            }
          });
          this.getDetails(e['title']);
          this.popupShow = true;
          break;
        case 'Edit Credit Note':
          if (this.selectedCreditListing.length == 0) {
            await this.$showAlert('error', 'No Credit Note Selected', 'Please select a credit note to edit');
          } else {
            if (this.selectedCreditListing['0']['Indicator'] == 'C') {
              await this.$showAlert(
                'error',
                'Selected Credit Note already cancelled!',
                'You are not allowed to edit cancelled credit note',
              );
              return;
            }

            const check = await this.$apiCallV2('/CreditNote/ValidateFINAR', {
              docno: this.selectedCreditListing[0]['Credit No'],
              doctype: 'C',
              action: 'E',
            });

            if (check[0]['Results'] == '1') {
              const msg = check[0]['ErrMsg'].split(':');
              await this.$showAlert('error', `${msg[0]}`, `${msg[1] ?? ''}`);
              return;
            }

            this.chargesDetailsIndex = -1;
            this.action = 'E';
            this.editButtons = [{ text: 'Edit', index: 0, width: 60 }];
            this.viewMode = false;
            this.openSelectFromDN = false;
            this.openSelectFromDoc = false;
            this.openNewSetDet = false;
            this.openNewSet = false;
            await this.getDetails(e['title']);
            this.popupShow = true;
            this.loadSumOfEvent();
          }
          break;
        case 'Delete Credit Note':
          if (this.selectedCreditListing.length == 0) {
            await this.$showAlert('error', 'No Credit Note Selected', 'Please select a credit note to delete');
          } else {
            if (this.selectedCreditListing['0']['Indicator'] == 'C') {
              await this.$showAlert(
                'error',
                'Selected Credit Note already cancelled!',
                'You are not allowed to delete cancelled credit note',
              );
              return;
            }

            const check = await this.$apiCallV2('/CreditNote/ValidateFINAR', {
              docno: this.selectedCreditListing[0]['Credit No'],
              doctype: 'C',
              action: 'E',
            });

            if (check[0]['Results'] == '1') {
              const msg = check[0]['ErrMsg'].split(':');
              await this.$showAlert('error', `${msg[0]}`, `${msg[1] ?? ''}`);
              return;
            }

            this.action = 'D';
            let confirmCancel = await this.$showAlert(
              'warning',
              'Deleting Credit Note Record',
              `Are you sure you want to delete credit note ${this.selectedCreditListing[0]['Credit No']} ?`,
              [
                { text: 'Yes', color: 'indianred', textColor: 'white' },
                { text: 'No', color: 'lightgray', textColor: 'black' },
              ],
            );
            if (confirmCancel == 'Yes') {
              this.showDeleteInvoicePopupWithRemarks = true;
            }
          }
          break;
        case 'View Credit Note':
          if (this.selectedCreditListing.length == 0) {
            await this.$showAlert('error', 'No Credit Note Selected', 'Please select a credit note to view');
          } else {
            this.action = '';
            this.viewMode = true;
            this.openSelectFromDN = false;
            this.openSelectFromDoc = false;
            this.openNewSetDet = false;
            this.openNewSet = false;
            await this.getDetails(e['title']);
            this.popupShow = true;
            this.loadSumOfEvent();
          }
          break;
        case 'Print Credit Note':
          if (this.selectedCreditListing.length === 0) {
            this.openPrintCNPage();
          } else {
            // only take the first selected row, will need to change logic if multiple selections are allowed in the future
            this.openPrintCNPage(this.selectedCreditListing[0]['Credit No']);
          }
          break;
        case 'Reinstate Credit Note':
          if (this.selectedCreditListing.length == 0) {
            await this.$showAlert('error', 'No Credit Note Selected', 'Please select a credit note to reinstate');
            return;
          } else {
            let confirmCancel = await this.$showAlert(
              'warning',
              'Reinstate Credit Note',
              `Are you sure you want to reinstate for this Credit Note ${this.selectedCreditListing[0]['Credit No']} ?`,
              [
                { text: 'Yes', color: 'indianred', textColor: 'white' },
                { text: 'No', color: 'lightgray', textColor: 'black' },
              ],
            );
            switch (confirmCancel) {
              case 'Yes': {
                this.$fullscreenLoading('Reinstate Selected Credit Note');
                let reinstateResult = await this.$apiCallV2('/CreditNote/SaveCreditNote', {
                  action: 'E',
                  reinstate: '1',
                  creditno: this.selectedCreditListing[0]['Credit No'],
                });
                this.$fullscreenLoading();
                if (reinstateResult[0]['Results'] == 0) {
                  this.$showAlert(
                    'success',
                    'Reinstated!',
                    `Credit Note ${this.selectedCreditListing[0]['Credit No']} was successfully reinstated.`,
                  );
                  this.creditListing = [];
                  this.selectedCreditListing = [];
                  this.$nextTick(async () => await this.refreshClicked());
                } else {
                  this.$showAlert('error', 'Reinstate unsuccessful', `${reinstateResult[0]['ErrMsg']}`);
                  this.creditListing = [];
                  this.selectedCreditListing = [];
                  this.$nextTick(async () => await this.refreshClicked());
                }
                break;
              }
              case 'No': {
                return;
              }
            }
          }
          break;
        case 'Hide Cancelled Charges':
          this.menu[6]['title'] = 'Show Cancelled Charges';
          this.menu[5]['enabled'] = false;
          this.menu[6]['icon'] = 'https://dsmlib.logistics.systems/Icons/GenericFile.png';
          this.$nextTick(async () => await this.refreshClicked());
          break;
        case 'Show Cancelled Charges':
          this.menu[6]['title'] = 'Hide Cancelled Charges';
          this.menu[5]['enabled'] = this.menu[5]['oriEnabled'] ? true : false;
          this.menu[6]['icon'] = 'https://dsmlib.logistics.systems/Icons/hide-file.png';
          this.$nextTick(async () => await this.refreshClicked());
          break;
      }
    },
    // Behaviour after clicking 'Delete' on delete popup
    deletePopupClicked: async function (e) {
      switch (e) {
        case 'Delete':
          if (!this.deleteInvoiceRemarks) {
            // Check if delete remarks entered
            this.deleteInvoiceErr = 'Please enter delete remarks!';
            return;
          } else {
            this.$fullscreenLoading('Deleting Selected Employee');
            let deleteResult = await this.$apiCallV2('/CreditNote/SaveCreditNote', {
              system: 'FINAR',
              action: 'D',
              txntype: '0',
              creditno: this.selectedCreditListing[0]['Credit No'],
              reason: this.deleteInvoiceRemarks,
            });
            this.$fullscreenLoading();
            this.deleteInvoiceRemarks = '';
            this.showDeleteInvoicePopupWithRemarks = false;
            if (deleteResult[0]['Results'] == 0) {
              this.$showAlert(
                'success',
                'Deleted!',
                `Credit Note ${this.selectedCreditListing[0]['Credit No']} was successfully deleted.`,
              );
              this.creditListing = [];
              this.selectedCreditListing = [];
              this.$nextTick(async () => await this.refreshClicked());
            } else {
              this.$showAlert('error', 'Delete unsuccessful', `${deleteResult[0]['ErrMsg']}`);
              this.creditListing = [];
              this.selectedCreditListing = [];
              this.$nextTick(async () => await this.refreshClicked());
            }
          }
          break;
        case 'Cancel':
          this.deleteInvoiceRemarks = '';
          this.showDeleteInvoicePopupWithRemarks = false;
          break;
      }
    },
    // API Call to retrieve billing customer list based on credit note type (job type id)
    getAPICallUponSelectInvoice: async function (val) {
      this.jobtypeid = val['JobTypeID'].toString();
      this.header['billingCustList'] = await this.$apiCallV2('/CreditNote/GetBillingCust', {
        doctype: 'C',
        jobtypeid: val['JobTypeID'].toString(),
      });
      if (this.action != 'N') {
        this.selectedBillCustomer = this.header['billingCustList'].find(
          (bill) => bill['Customer Code'].trim() == this.creditNote[0]['Customer No'],
        );
      }
    },
    // Get billing customer list after selecting credit note type
    creditTypeIsSelect: async function (val) {
      if (val) {
        this.$fullscreenLoading('Getting Billing Customer');
        await this.getAPICallUponSelectInvoice(val);
        this.viewMode = false;
        this.$fullscreenLoading();
      }
    },
    // Load summary of events (LHDN status, credit note indicator, print count, authorised/unauthorise)
    loadSumOfEvent: function () {
      for (let key in this.creditNote[0]) {
        if (this.creditNote[0].hasOwnProperty(key)) {
          let indicatorName = this.getIndicatorName(key, this.creditNote[0][key], 'title');
          let backgroundColor = this.getIndicatorName(key, this.creditNote[0][key], 'background');
          let element = document.getElementById(key);
          if (element) {
            element.textContent = indicatorName;
            element.style.backgroundColor = backgroundColor;
          }
        }
      }
    },
    // To populate title and get details if edit or view
    getDetails: async function (title) {
      this.popupTitle = title;
      if (this.selectedCreditListing.length > 0 && this.action != 'N') {
        await this.populateCreditDetails(this.selectedCreditListing[0]['Credit No']);
      }
      this.popupShow = true;
    },
    // To populate credit note details
    populateCreditDetails: async function (creditno) {
      this.$fullscreenLoading('Getting data');
      let dataToPost = {
        txntype: '',
        creditno: creditno,
        doctxn: '3',
      };
      this.creditNote = (await this.$apiCallV2('/CreditNote/GetCreditDetails', dataToPost))['CreditNoteDetails'][
        'CreditHeader'
      ];
      this.createdBy = `${this.creditNote[0]['UserCreated'] ? this.creditNote[0]['UserCreated'].trim() : ''} ${this.creditNote[0]['CreatedDate'] ? new Date(this.creditNote[0]['CreatedDate']).formatString('dd/MM/yyyy HH:mm') : ''}`;
      this.updatedBy = `${this.creditNote[0]['UserID'] ? this.creditNote[0]['UserID'].trim() : ''} ${this.creditNote[0]['DateUpdated'] ? new Date(this.creditNote[0]['DateUpdated']).formatString('dd/MM/yyyy HH:mm') : ''}`;
      // #region header section
      this.creditNote[0]['IRBM Ref No'] = 'P';
      if (!this.creditNote[0]?.['IRBM']) {
        this.creditNote[0]['IRBM Ref No'] = 'P';
      }
      if (!this.creditNote[0]?.['PrintCount']) {
        this.creditNote[0]['PrintCount'] = '0';
      }
      this.selectedCreditTypeList = this.header['creditTypeList'].find(
        (item) => item.JobTypeID == this.creditNote[0]['JobConfig'],
      );
      await this.getAPICallUponSelectInvoice(this.selectedCreditTypeList);
      let data = {
        doctype: 'C',
        jobtypeid: this.jobtypeid,
        table: 'CREDITNOTE',
        billcu_no: this.creditNote[0]['Customer Code'],
        inv_no: this.creditNote[0]['Credit No'],
        top: this.rowLimitInputValue,
      };
      this.oldRate = this.creditNote[0]['Rate'];
      this.jobnoList = await this.$apiCallV2(`/CreditNote/GetQueryByJobConfig`, data);
      this.selectedReasonCode =
        !this.creditNote?.[0]['Remarks4'] ?
          null
        : this.reasonCodeList.find((bill) => bill['ReasonCode'].trim() == this.creditNote[0]['Remarks4']);
      let cur = this.creditno?.[0]['CreditAmount'].toString() ?? '';
      this.totalInWords = this.convertCurrencyToWords(cur);
      this.headerSelectedCurrency = this.header['currencyList'].getSelected(
        this.creditNote[0]['Currency'],
        'Currency Code',
      );
      this.oldCurrency = this.headerSelectedCurrency;
      this.currencySelected(this.headerSelectedCurrency, this.creditNote[0]['Rate']);
      this.header['creditNoteDate'] = new Date(this.creditNote[0]['Date']);
      this.header['creditNoteNo'] = this.creditNote[0]['Credit No'];
      this.header['invoiceRem1'] = this.creditNote[0]['Remarks'];
      this.header['invoiceRem2'] = this.creditNote[0]['Remarks2'];
      this.header['invoiceRem3'] = this.creditNote[0]['Remarks3'];
      this.header['selectedCreditType'] =
        this.header['creditTypeList'].find((item) => item['Credit Type'] == this.creditNote['CreditType']) ?? null;
      this.newDetailsList = this.creditNote[0]['Details'] ?? [];
      this.settlementList = this.creditNote[0]['Settlement'] ?? [];
      // #endregion

      // #region Addtional Info section
      this.companyName = this.creditNote[0]['Delivery']?.[0]['Company Name'] ?? '';
      this.companyTIN = this.creditNote[0]['Delivery']?.[0]['Company Tin'] ?? '';
      this.companyRegNo = this.creditNote[0]['Delivery']?.[0]['Company ROC'] ?? '';
      this.defaultAddressData['addressLine1'] = this.creditNote[0]['Delivery']?.[0]['Addr1'] ?? '';
      this.defaultAddressData['addressLine2'] = this.creditNote[0]['Delivery']?.[0]['Addr2'] ?? '';
      this.defaultAddressData['addressLine3'] = this.creditNote[0]['Delivery']?.[0]['Addr3'] ?? '';
      this.defaultAddressData['addressLine4'] = this.creditNote[0]['Delivery']?.[0]['Addr4'] ?? '';
      this.defaultAddressData['postcode'] = this.creditNote[0]['Delivery']?.[0]['PostCode'] ?? '';
      this.defaultAddressData['city'] = this.creditNote[0]['Delivery']?.[0]['City'] ?? '';
      this.defaultAddressData['state'] = this.creditNote[0]['Delivery']?.[0]['State'] ?? '';
      this.defaultAddressData['country'] = this.creditNote[0]['Delivery']?.[0]['Country'] ?? '';
      this.leftSideText = this.creditNote[0]['ReportHeader']?.[0]['JobInfoLeft'] ?? '';
      this.rightSideText = this.creditNote[0]['ReportHeader']?.[0]['JobInfoRight'] ?? '';

      let forjobList = this.creditNote[0]['Details']?.filter((job) => job['Job No'].trim() != '') ?? []; //filter empty job

      this.settlementDocNoList = await this.$apiCallV2('/CreditNote/GetSettlementDocNo', {
        billcu_no: this.selectedBillCustomer['Customer Code'],
        currency: this.headerSelectedCurrency?.['Currency Code'],
        creditno: this.creditNote[0]['Credit No'],
        hidefilter: 'Y',
      });
      if (forjobList.length > 0) {
        this.jobList = [];
        let current = '';
        forjobList.forEach((job) => {
          if (current != job['Job No'].trim()) {
            //filter duplicate job
            current = job['Job No'];
            this.jobList.push({ JobNo: job['Job No'] });
          }
        });
      }

      if (this.creditNote[0]['Job No']) {
        this.selectedJobInfo = this.jobList.getSelected(this.creditNote[0]['Job No'], 'JobNo');
      }

      if (this.leftSideText != '' || this.rightSideText != '') {
        this.selectedHeaderDesc = this.headerDescRadioButtons[1];
        this.reportHdrCheck = true;
        this.showFromTemplate = true;
        this.showInfoFromJob = false;
      } else if (this.selectedJobInfo) {
        this.selectedHeaderDesc = this.headerDescRadioButtons[0];
        this.reportHdrCheck = true;
        this.showInfoFromJob = true;
        this.showFromTemplate = false;
      } else {
        this.selectedHeaderDesc = null;
        this.reportHdrCheck = false;
        this.selectedHeaderDesc = null;
        this.showInfoFromJob = false;
        this.showFromTemplate = false;
      }

      // #endregion

      // #region Attachment section
      this.documentsList = this.creditNote[0]['Attachment'] ?? [];
      // #endregion

      // #region Payment section
      // #endregion

      // #region Events section
      this.selectedSalesTeamList = this.salesTeamList.find(
        (item) => item['Sales Team Code'] == this.creditNote[0]['SalesPerson']?.[0]['Sales Team'],
      );
      if (!this.selectedSalesTeamList) {
        this.freeTextSalesTeam = this.creditNote[0]['SalesPerson']?.[0]['Sales Team'];
      }
      this.selectedSalesPersonList = this.salesPersonList.find(
        (item) => item['Sales Person Code'] == this.creditNote[0]['SalesPerson']?.[0]['Sales Person'],
      );
      if (!this.selectedSalesPersonList) {
        this.freeTextSalesPerson = this.creditNote[0]['SalesPerson']?.[0]['Sales Person'];
      }
      this.glEntryList = this.creditNote?.[0]['GLEntry'] ?? [];
      this.offsetDocList = this.creditNote?.[0]['Offset'] ?? [];
      this.eInvoiceList = this.creditNote?.[0]['eInvoice'] ?? [];
      if (this.eInvoiceList.length > 0) {
        let getHighestId = this.eInvoiceList.reduce((max, item) => {
          return item['#'] > max ? item['#'] : max;
        }, 0);
        let getLatestObj = this.eInvoiceList.find((obj) => obj['#'] == getHighestId);
        this.creditNote[0]['LHDN Submission'] = getLatestObj?.['Status'][0];
      } else {
        this.creditNote[0]['LHDN Submission'] = 'P';
      }
      // #endregion
      this.calculateTotalCredit();

      this.$fullscreenLoading();
    },
    focusOut: function (state, type) {
      if (!state) {
        if (type == 'fnetamt') {
          // To calculate fnet amount
          if (this.details['famount']) {
            this.fnetamt = (Number(this.details['famount']) + Number(this.ftaxableamt)).toString();
          } else {
            this.fnetamt = '0.00';
          }
        }
      }
    },
    // After click "Select items from Inv/DN" (inside add details)
    openSubPackage: async function (type) {
      if (type == 'Inv') {
        if (!this.selectedInvDN) {
          await this.$showAlert('error', 'No Inv/DN Selected', 'Please select a Inv/DN to proceed');
          return;
        }
        this.subPackageTitle = `Select items from Inv/DN ${this.selectedInvDN ? this.selectedInvDN['Inv No'] : ''}`;
        this.selectedSubDetails = [];
        this.openSelectFromDN = true;
        this.openNewSetDet = false;
      } else {
        let dataToPost = {
          billcu_no: this.selectedBillCustomer['Customer Code'] ?? '',
          currency: this.headerSelectedCurrency['Currency Code'] ?? '',
          creditno: this.header['creditNoteNo'].includes('*') ? '' : this.header['creditNoteNo'],
          exrate: this.header['exchRate'] ?? '',
          hidefilter: 'Y',
        };

        this.$fullscreenLoading('Getting Doc List');
        let temp = await this.$apiCallV2('/CreditNote/GetSettlementOffset', dataToPost);
        this.$fullscreenLoading();
        let tempAll = this.settlementList.concat(this.deletedSettlementList);

        temp.forEach((item, index) => {
          item['#'] = tempAll.length + index + 1;
        });
        this.settlementList.forEach((toCheck) => {
          temp = temp.filter((item) => item['Inv No'] != toCheck['Inv No']);
        });
        this.fromDocList = [];
        this.$nextTick(() => {
          this.allFromSettlementOffset = temp;
          this.fromDocList = temp;
          this.subPackageTitle = `Select Offset Invoice`;
          this.selectedFromDocList = [];
          this.openSelectFromDoc = true;
          this.openNewSet = false;
        });
      }
    },
    // Behaviour after Add/Edit charges (credit note detail)
    addEditCharges: async function (type) {
      this.clearErrMessage();
      switch (type) {
        // add edit for details
        case 'details':
          let temp = this.newDetailsList;
          let tempAll = this.newDetailsList.concat(this.deletednewDetailsList);
          let error = false;
          if (this.details['tax'] != '0' && !this.selectedInvDN) {
            // check if inv/dn selected when service tax > 0
            this.invDNError = 'Inv./No is compulsory for Service Tax more than 0!';
            this.requiredError.push({ key: 'invDNError', value: 'Inv./No is compulsory for Service Tax more than 0!' });
            error = true;
          }
          if (!this.selectedChargesCode) {
            // check if charge code is selected
            this.selectedChargesCodeErr = 'Charges Code is required!';
            this.requiredError.push({ key: 'chargesCode', value: 'Charges Code is required!' });
            error = true;
          }
          if (!this.details['famount']) {
            this.famountErr = 'Credit Amount is required!';
            this.requiredError.push({ key: 'famount', value: 'Credit Amount is required!' });
            error = true;
          }
          if (!this.details['tax']) {
            this.taxErr = 'Srv. Tax % is required!';
            this.requiredError.push({ key: 'tax', value: 'Srv. Tax % is required!' });
            error = true;
          }
          if (!this.details['gst2taxrate']) {
            this.gst2taxrateErr = 'Tax % is required!';
            this.requiredError.push({ key: 'gst2taxrate', value: 'Tax % is required!' });
            error = true;
          }
          if (!this.details['ftaxable_amountgst2'] && this.GSTEnabled) {
            this.ftaxable_amountgst2Err = 'Tax Amt is required!';
            this.requiredError.push({ key: 'ftaxable_amountgst2', value: 'Tax Amt is required!' });
            error = true;
          }
          if (!this.fnetamt) {
            this.fnetamtErr = 'Net Amount is required!';
            this.requiredError.push({ key: 'fnetamt', value: 'Net Amount is required!' });
            error = true;
          }
          if (this.selectedInvDN) {
            let detailAmt =
              this.selectedNewDetails.length == 0 ? 0 : Number(this.selectedNewDetails?.[0]['Net FAmount']);
            let tempCreditedAmt =
              this.selectedNewDetails.length == 0 ?
                Number(this.selectedInvDN['Credited Amount']).toFixed(2)
              : Number(this.selectedInvDN['Credited Amount']).toFixed(2) - parseFloat(detailAmt);
            // let availableVal = (Number(this.selectedInvDN['F.Total Amount']) - Number(tempCreditedAmt)).toFixed(2);
            let existingInv =
              this.selectedNewDetails.length > 0 ?
                this.selectedNewDetails?.[0]['Inv No'] === this.selectedInvDN?.['Inv No']
              : false;
            let availableVal = (
              Number(this.selectedInvDN['F.Total Amount']) - Number(this.selectedInvDN['Credited Amount'])
            ).toFixed(2);

            if (
              parseFloat(this.details['famount']) > parseFloat(this.selectedInvDN['F.Total Amount']) ||
              (parseFloat(this.details['famount']) > availableVal && !existingInv)
            ) {
              // if(((parseFloat(this.details['famount']) > parseFloat(this.selectedInvDN['F.outstanding'])) )){
              await this.$showAlert(
                'error',
                'Unable to add or edit Detail',
                'Detail Amount value is more than total Credited Amount',
              );

              this.$nextTick(() => {
                this.details['famount'] =
                  existingInv ? Number(this.selectedInvDN['F.Total Amount']).toString() : availableVal.toString();
              });
              return;
            }
          }
          if (parseFloat(this.details['famount']) < 0) {
            this.famountErr = 'Credit Amount is invalid!';
            this.requiredError.push({ key: 'famount', value: 'Credit Amount is invalid!' });
            error = true;
          }

          if (error) {
            return;
          }
          const highestDetId = tempAll.reduce((max, item) => {
            return Math.max(max, parseInt(item['SeqNo'], 10));
          }, 0);
          const highestDethash = tempAll.reduce((max, item) => {
            return Math.max(max, parseInt(item['#'], 10));
          }, 0);
          this.newDetailsList = [];
          let details = {
            '#':
              (this.action == 'E' && this.selectedNewDetails.length == 0) || this.action == 'N' ?
                Number(highestDethash) + 1
              : this.selectedNewDetails?.['0']['#'], //#
            Action:
              this.selectedNewDetails.length == 0 ? 'N' : (this.selectedNewDetails?.['0']['Action'] ?? this.action),
            SeqNo:
              this.selectedNewDetails.length > 0 ? this.selectedNewDetails?.[0]['SeqNo'] : Number(highestDetId) + 1, //seqno
            'Charges Code': this.selectedChargesCode?.['Charge Code'] ?? '', //chargescode
            'Inv No': this.selectedInvDN?.['Inv No'] ?? '', //inv_no
            'Job No':
              this.selectedJobno ? this.selectedJobno?.['Job No']
              : this.freeTextJobNo != '' ? this.freeTextJobNo
              : '', //job_no
            'CN #': this.selectedCNNO?.['Consignment No'] ?? '', //consignementno
            'Cost Code': this.selectedCostCode?.['Cost Code'] ?? '', //costcode
            Tax: this.details['tax'] ?? '0', //tax
            'Credit Amount': this.myrEquivalentCredit ? this.myrEquivalentCredit.toString() : '0', //amount
            'Srv Tax Amount': this.taxableamt ?? '', //taxableamt
            'Net Amount': this.netamt ?? '', //netamt
            'Credit FAmount': this.details['famount'] ?? '', //famount
            'Srv Tax FAmount': this.ftaxableamt ?? '', //ftaxableamt
            'Net FAmount': this.fnetamt ?? '', //fnetamt
            Remark: this.details['detail_remark'] ?? '', //detail_remark
            Remark2: this.details['detail_remark2'] ?? '', //detail_remark2
            Remark3: this.details['detail_remark3'] ?? '', //detail_remark3
            Description: this.selectedChargesCode?.['Description'] ?? '', //chargescode
            'Tax Code':
              this.GSTEnabled ? this.selectedTaxCode?.['Tax Code'] : (this.selectedsstTaxCode?.['Tax Code'] ?? ''), //gst2taxcode
            TxnType: this.selectedInvDN?.['Type'],
            Currency: this.headerSelectedCurrency['Currency Code'],
            Rate: this.header['exchRate'],
            'User ID': '',
            DateUpdated: '',
          };
          if (this.GSTEnabled) {
            details['Tax %'] = this.details['gst2taxrate'] ?? ''; //gst2taxrate
            details['FTax Amt'] = this.details['ftaxable_amountgst2'] ?? ''; //ftaxable_amountgst2
            details['Tax Amt'] = this.details['taxable_amountgst2'] ?? ''; //taxable_amountgst2
          }
          if (this.selectedNewDetails.length > 0) {
            // let index = this.action == 'N' ? temp.findIndex(obj => obj['#'] == this.selectedNewDetails?.[0]['#']) : temp.findIndex(obj => obj['SeqNo'] == this.selectedNewDetails?.[0]['SeqNo']);
            details['invTxnID'] = this.selectedNewDetails?.[0]['invTxnID'];
            temp.splice(this.chargesDetailsIndex, 1, details);
          } else {
            temp.push(details);
          }

          this.$nextTick(() => {
            this.newDetailsList = temp;
            this.clearChargesDetails();
            if (this.action == 'E') {
              this.openNewSetDet = false;
            }
          });
          this.invDNList = await this.$apiCallV2(`/CreditNote/GetInvDNNo`, {
            billcu_no:
              this.selectedBillCustomer ?
                this.selectedBillCustomer['Customer Code']
              : this.creditNote[0]['Customer No'],
            currency: this.headerSelectedCurrency['Currency Code'],
            hidefilter: 'Y',
          });

          break;
        // add edit for settlement
        case 'settlement':
          let tempSettle = this.settlementList;
          let tempSettleAll = this.settlementList.concat(this.deletedSettlementList);
          let errortempSettle = false;
          if (!this.selectedSettlementDocNo) {
            this.settlementDocNoErr = 'Doc No is required!';
            this.requiredError.push({ key: 'settlementDocNoErr', value: 'Doc No is required!' });
            errortempSettle = true;
          }
          if (!this.foffsetAmt) {
            this.foffsetAmtErr = 'Offset is required!';
            this.requiredError.push({ key: 'foffsetAmtErr', value: 'Offset is required!' });
            errortempSettle = true;
          }
          if (parseFloat(this.foffsetAmt) < 0 || this.foffsetAmt == '0.00') {
            this.foffsetAmtErr = 'Offset is invalid!';
            this.requiredError.push({ key: 'foffsetAmtErr', value: 'Offset is invalid!' });
            errortempSettle = true;
          }
          if (parseFloat(this.offsetAmt) < 0) {
            await this.$showAlert('error', 'Invalid Offset Amount', 'Offset amount must be more than 0');
            return;
          }
          // let checkVal = parseFloat(this.offsetAmt) > parseFloat(this.totalAmt);
          // if(checkVal){
          //   await this.$showAlert('error', 'Unable to add settlement', 'Offset Amount is more than Credited Amount');
          //   return;
          // }
          if (this.selectedSettlement.length > 0) {
            if (
              parseFloat(this.foffsetAmt) > parseFloat(this.balanceAmt.replaceAll(',', '')) &&
              this.selectedSettlement?.['0']['Inv No'] != this.selectedSettlementDocNo?.['Offset Doc']
            ) {
              await this.$showAlert(
                'error',
                'Unable to add selected Invoice',
                'Offset Amount cannot be greater than Credit Note Balance',
              );
              return;
            }
          }
          if (this.selectedSettlementDocNo) {
            const offsetAmt = parseFloat(this.foffsetAmt);
            let reformat = Number(this.fbalanceAmt.replaceAll(',', ''));
            const balanceAmt =
              this.selectedSettlement.length > 0 ?
                reformat + Number(this.selectedSettlement[0]['Offset FAmount'])
              : parseFloat(reformat);
            const outstandingAmt = parseFloat(this.selectedSettlementDocNo['F.outstanding']);

            if (offsetAmt > balanceAmt || offsetAmt > outstandingAmt) {
              const errorMsg =
                offsetAmt > balanceAmt ?
                  'Offset Amount value is more than total Credit Balance'
                : 'Offset Amount value is more than total Invoice Outstandings Balance';

              await this.$showAlert('error', 'Unable to add or edit Detail', errorMsg);

              this.$nextTick(() => {
                this.foffsetAmt = Math.min(outstandingAmt, balanceAmt).toFixed(2);
              });
              return;
            }
          }
          if (errortempSettle) {
            return;
          }
          const highestId = tempSettleAll.reduce((max, item) => {
            return item['SeqNo'] > max ? item['SeqNo'] : max;
          }, 0);
          this.settlementList = [];
          let settlement = {
            '#':
              this.action == 'N' ? tempSettleAll.length + 1
              : this.selectedSettlement.length > 0 ? this.selectedSettlement['0']['SeqNo']
              : tempSettleAll.length + 1, //#
            Action:
              this.selectedSettlement.length == 0 ? 'N'
              : this.selectedSettlement?.['0']['Action'] ? this.selectedSettlement?.['0']['Action']
              : this.action, //"action": "N",
            SeqNo:
              this.action == 'E' && this.selectedSettlement.length > 0 ?
                this.selectedSettlement?.['0']['SeqNo']
              : Number(highestId) + 1,
            'Inv No': this.selectedSettlementDocNo?.['Offset Doc'] ?? '', //"inv_no": "testing123",
            'Job No': this.selectedSettlementDocNo?.['JobNo'] ?? '', //"jobno": "testjobno",
            'Offset Amount': this.offsetAmt ?? '', //"offset_amount": "1000.9999",
            'Offset FAmount': this.foffsetAmt ?? '', //"foffset_amount": "100.9999",
            Remark: this.setRem1 ?? '', //"detail_remark": "test1",
            Remark2: this.setRem2 ?? '', //"detail_remark2": "test2",
            Remark3: this.setRem3 ?? '', //"detail_remark3": "test3"
            Currency: this.headerSelectedCurrency['Currency Code'],
            Rate: this.header['exchRate'],
            TxnType: this.selectedSettlementDocNo?.['Type'],
            'User ID': '',
            DateUpdated: '',
          };
          this.$nextTick(() => {
            if (this.action == 'E' && this.selectedSettlement.length > 0) {
              settlement['#'] = this.selectedSettlement['0']['#'];
              tempSettle.splice(this.chargesDetailsIndex, 1, settlement);
            } else {
              tempSettle.push(settlement);
            }
            this.settlementList = tempSettle;
            this.clearChargesDetails();
            this.chargesDetailsIndex = -1;
            if (this.action == 'E') {
              this.openNewSet = false;
            }
          });

          break;
        case 'fromDetails': //settlement copied from details list.
          let getAllSet = this.settlementList.concat(this.deletedSettlementList);
          const getHighestId = getAllSet.reduce((max, item) => {
            return item['SeqNo'] > max ? item['SeqNo'] : max;
          }, 0);

          const invoiceMap = this.newDetailsList.reduce((acc, item) => {
            const invNo = item['Inv No'];
            // remove data without inv no
            if (!acc[invNo] && invNo) {
              acc[invNo] = { netAmount: 0, netFAmount: 0, jobno: item['Job No'] };
              acc[invNo].netAmount += parseFloat(item['Net Amount']);
              acc[invNo].netFAmount += parseFloat(item['Net FAmount']);
            }
            return acc;
          }, {});
          // If an Inv No exists in settlementList but not in newDetailsList, leave it unchanged
          if (this.settlementList.length === 0) {
            for (let invNo of Object.keys(invoiceMap)) {
              let checkBal = this.settlementDocNoList.find((item) => item['Offset Doc'] === invNo);
              let tempTotalDeletedOffsetAmt =
                this.deletedSettlementList.length == 0 ?
                  0
                : this.deletedSettlementList
                    .map((data) => {
                      return Number(data['Offset Amount']);
                    })
                    .reduce((a, b) => {
                      return a + b;
                    });
              const getIndex = Object.keys(invoiceMap).indexOf(invNo);
              const matchingItem = {
                Action: 'N',
                '#': getAllSet.length + 1,
                'Inv No': invNo,
                'Offset Amount': invoiceMap[invNo].netAmount,
                'Offset FAmount': invoiceMap[invNo].netFAmount,
                SeqNo: Number(getHighestId) + Number(getIndex) + 1,
                'Job No': invoiceMap[invNo].jobno ?? '', //"jobno": "testjobno",
                Remark: '', //"detail_remark": "test1",
                Remark2: '', //"detail_remark2": "test2",
                Remark3: '', //"detail_remark3": "test3"
                'User ID': '',
                DateUpdated: '',
              };

              this.settlementList.push(matchingItem);
            }
            this.currentTabIndex = 1;
          } else {
            // Copy over objects from newDetailsList to settlementList if Inv No doesn't exist in settlementList
            for (let invNo of Object.keys(invoiceMap)) {
              const matchingIndex = this.settlementList.findIndex((item) => item['Inv No'] === invNo);
              const getIndex = Object.keys(invoiceMap).indexOf(invNo);
              let tempTotalOffsetAmt =
                this.settlementList.length == 0 ?
                  0
                : this.settlementList
                    .map((data) => {
                      return Number(data['Offset Amount']);
                    })
                    .reduce((a, b) => {
                      return a + b;
                    });
              let totalOffsetToCheck = tempTotalOffsetAmt + invoiceMap[invNo].netAmount;
              if (parseFloat(totalOffsetToCheck) > parseFloat(this.balanceAmt.replaceAll(',', ''))) {
                await this.$showAlert(
                  'error',
                  'Unable to add selected Invoice',
                  'Offset Amount cannot be greater than Credit Note Balance',
                );
                return;
              }
              if (matchingIndex === -1) {
                // Inv No doesn't exist in settlementList, so add it
                this.settlementList.push({
                  Action: 'N',
                  '#': getAllSet.length + 1,
                  'Inv No': invNo,
                  'Offset Amount': invoiceMap[invNo].netAmount,
                  'Offset FAmount': invoiceMap[invNo].netFAmount,
                  SeqNo: Number(getHighestId) + Number(getIndex) + 1,
                  'Job No': invoiceMap[invNo].jobno ?? '', //"jobno": "testjobno",
                  Remark: '', //"detail_remark": "test1",
                  Remark2: '', //"detail_remark2": "test2",
                  Remark3: '', //"detail_remark3": "test3"
                  'User ID': '',
                  DateUpdated: '',
                });
              } else {
                // Inv No exists, update the Offset Amounts
                let tempTotalDeletedOffsetAmt =
                  this.deletedSettlementList.length == 0 ?
                    0
                  : this.deletedSettlementList
                      .map((data) => {
                        return Number(data['Offset Amount']);
                      })
                      .reduce((a, b) => {
                        return a + b;
                      });
                let totalOffsetAmt =
                  tempTotalOffsetAmt - Number(invoiceMap[invNo].netAmount) - Number(tempTotalDeletedOffsetAmt);
                if (parseFloat(totalOffsetAmt) > parseFloat(this.balanceAmt.replaceAll(',', ''))) {
                  await this.$showAlert(
                    'error',
                    'Unable to add selected Invoice',
                    'Offset Amount cannot be greater than Credit Note Balance',
                  );
                  return;
                }

                this.settlementList[matchingIndex]['Offset Amount'] = invoiceMap[invNo].netAmount;
                this.settlementList[matchingIndex]['Offset FAmount'] = invoiceMap[invNo].netFAmount;
              }
            }

            this.currentTabIndex = 1;
          }
          break;
        case 'copySettle': //copy
          if (this.selectedFromDocList.length > 0) {
            const check = this.settlementList.find((item) => item['#'] == this.selectedFromDocList[0]['#']);
            let totalOffsetAmt = this.selectedFromDocList
              .map((data) => {
                return Number(data['Offset Amount']);
              })
              .reduce((a, b) => {
                return a + b;
              });

            if (parseFloat(totalOffsetAmt) > parseFloat(this.balanceAmt.replaceAll(',', ''))) {
              await this.$showAlert(
                'error',
                'Unable to add selected Invoice',
                'Offset Amount cannot be greater than Credit Note Balance',
              );
              return;
            }
            if (parseFloat(this.selectedFromDocList[0]['Offset Amount']) < 0) {
              await this.$showAlert('error', 'Invalid Offset Amount', 'Offset amount must be more than 0');
              return;
            }
            if (check) {
              this.$showAlert('error', 'Duplicated item', 'Selected item has already been added.');
            } else {
              let temp = this.settlementList;
              let tempSub = this.fromDocList;
              this.settlementList = [];
              let tempAll = temp.concat(this.deletedSettlementList);
              const highestDetId = tempAll.reduce((max, obj) => {
                return Math.max(max, parseInt(obj['SeqNo'], 10));
              }, 0);
              this.selectedFromDocList.forEach((item) => {
                let getIndex = this.selectedFromDocList.indexOf(item);
                temp.push({
                  '#': item['#'] ? item['#'] : tempAll.length + 1, //#
                  Action: 'N', //"action": "N",
                  SeqNo: Number(highestDetId) + Number(getIndex) + 1,
                  'Inv No': item['Inv No'] ?? '', //"inv_no": "testing123",
                  'Job No': item['Job No'], //"jobno": "testjobno",
                  'Offset Amount': item['Offset Amount'], //"offset_amount": "1000.9999",
                  'Offset FAmount': item['Original Amount'], //"foffset_amount": "100.9999",
                  Remark: '', //"detail_remark": "test1",
                  Remark2: '', //"detail_remark2": "test2",
                  Remark3: '', //"detail_remark3": "test3"
                  Copy: true,
                  'Credit No': this.header['creditNoteNo'],
                  TxnType: item['TxnType'],
                  Rate: item['Rate'],
                  Currency: item['Currency'],
                  'User ID': '',
                  DateUpdated: '',
                });
              });
              this.selectedFromDocList.forEach((copyItem) => {
                tempSub = tempSub.filter((item) => item['#'] != copyItem['#']);
              });

              this.fromDocList = [];
              this.$nextTick(() => {
                this.settlementList = temp;
                this.fromDocList = tempSub;
                this.selectedFromDocList = [];
              });
            }
          } else {
            this.$showAlert('error', 'No Item selected', 'Please select at least 1 item to add.');
          }
          break;
        default: //DETAILS COPIED FROM SELECTED INV ITEM
          if (this.selectedSubDetails.length > 0) {
            const check = this.newDetailsList.find((item) => item['#'] == this.selectedSubDetails[0]['#']);
            if (check) {
              this.$showAlert('warning', 'Duplicated Job Charges', 'Selected Job Charges has already been added.');
            } else {
              let temp = this.newDetailsList;
              let tempSub = this.fromInvList;
              this.newDetailsList = [];
              const highestDetId = temp.reduce((max, item) => {
                return Math.max(max, parseInt(item['SeqNo'], 10));
              }, 0);
              let tempAll = temp.concat(this.deletednewDetailsList);
              this.selectedSubDetails.forEach((item) => {
                let getIndex = this.selectedSubDetails.indexOf(item);
                temp.push({
                  '#': this.openSelectFromDN ? item['#'] : tempAll.length + 1, //#
                  Action: 'N', //action
                  SeqNo: Number(highestDetId) + Number(getIndex) + 1, //seqno
                  'Charges Code': item['Charge Code'] ?? '', //chargescode
                  'Inv No': this.selectedInvDN?.['Inv No'] ?? '', //inv_no
                  'Job No': item['Job No'] ?? '', //job_no
                  'CN #': item['Consignment No'] ?? '', //consignementno
                  'Cost Code': item?.['Cost Code'] ?? '', //costcode
                  Tax: item?.['Tax Rate'] ?? '', //tax
                  'Credit Amount': item?.['Amt Excl Tax'], //amount
                  'Srv Tax Amount': item?.['Tax Amt'] ?? '', //taxableamt
                  'Net Amount': item?.['Amt Incl Tax'] ?? '', //netamt
                  'Credit FAmount': item?.['fAmt Excl Tax'] ?? '', //famount
                  'Srv Tax FAmount': item?.['fTax Amt'] ?? '', //ftaxableamt
                  'Net FAmount': item?.['fAmt Incl Tax'] ?? '', //fnetamt
                  Currency: item?.['Currency'] ?? 'MYR',
                  Remark: this.details['detail_remark'] ?? '', //detail_remark
                  Remark2: this.details['detail_remark2'] ?? '', //detail_remark2
                  Remark3: this.details['detail_remark3'] ?? '', //detail_remark3
                  Description: item['Description'] ?? '',
                  'Tax Code': '', //gst2taxcode
                  invTxnID: item['InvTxnID'], //invtxnid
                  copy: item['InvTxnID'],
                  'User ID': '',
                  DateUpdated: '',
                });
              });
              this.selectedSubDetails.forEach((copyItem) => {
                tempSub = tempSub.filter((item) => item['#'] != copyItem['#']);
              });

              this.fromInvList = [];
              this.$nextTick(() => {
                this.newDetailsList = temp;
                this.fromInvList = tempSub;
                this.selectedSubDetails = [];
              });
            }
          } else {
            this.$showAlert('error', 'No Item selected', 'Please select at least 1 item to add.');
          }
          break;
      }
    },
    // Delete from attachment
    deleteDocument: function () {
      if (this.selectedDoc.length == 0) {
        this.$showAlert('error', 'No Document Selected', 'Please select at least 1 document to delete.');
      } else {
        let tempdeleted = this.deletedDocumentsList;
        this.deletedDocumentsList = [];

        for (const d of this.selectedDoc) {
          let document = {
            Action: 'D',
            'Document GUID': d['Document GUID'],
          };
          if (d['Document GUID']) {
            tempdeleted.push(document);
          }
        }

        let temp = this.documentsList;
        this.selectedDoc.forEach((s) => {
          let index = temp.map((n) => n['ID']).indexOf(s['ID']);
          temp.splice(index, 1);
        });
        this.documentsList = [];

        this.$nextTick(() => {
          this.deletedDocumentsList = tempdeleted;
          this.documentsList = temp;
          this.selectedDoc = [];
        });
      }
    },
    // Behavious when click buttons in attachments tab
    attachmentBtnClicked: async function (type) {
      this.attachmentsAction = type;
      this.documents['remarks'] = '';
      this.documents['docName'] = '';
      this.documents['attachmentFile'] = '';
      this.documents['filetype'] = '';
      this.documents['docGuid'] = '';
      this.documents['remarks'] = '';
      if (type == 'edit') {
        // Edit Attachments
        if (this.selectedDoc.length == 0) {
          await this.$showAlert('error', 'No Attachment Selected', 'Please select an Attachment to edit');
          if (this.viewMode) return;
          return;
        }
        this.attachmentsPopupButtons = [{ text: 'Edit' }, { text: 'Cancel', color: 'lightgray' }];
        // populate selected documents;
        this.documents['remarks'] = this.selectedDoc[0]['Doc Remark'];
        this.documents['docName'] = this.selectedDoc[0]['File Name'];
        this.documents['attachmentFile'] = this.selectedDoc[0]['Doc Image'];
        this.documents['filetype'] = this.selectedDoc[0]['File Type'];
        this.documents['docGuid'] = this.selectedDoc[0]['Document GUID'];
        this.documents['remarks'] = this.selectedDoc[0]['Doc Remark'];
        this.showAttachmentsPopup = true;
        this.attachmentsPopupTitle = 'Edit Attachment';
      } else if (type == 'add') {
        // Add Attachments
        this.attachmentsPopupButtons = [{ text: 'Add' }, { text: 'Cancel', color: 'lightgray' }];
        this.showAttachmentsPopup = true;
        this.attachmentsPopupTitle = 'Add Attachment';
      }
    },
    // Check if attachment can be view without downloading
    attachmentIsProjectable: function () {
      if (!this.attachmentProjectorFileName || !this.attachmentProjectorType) {
        // if either of these are blank
        let error = false;
        return false;
      }
      const filenameSplit = this.attachmentProjectorFileName.trim().split('.');
      const fileExtension = filenameSplit[filenameSplit.length - 1];
      const projectableExtensions = ['pdf', 'txt', 'json', 'mp3', 'wav', 'mp4', 'webm', 'ogg'];
      return this.attachmentProjectorType.startsWith('image/') || projectableExtensions.includes(fileExtension);
    },
    projectAttachment: async function (file) {
      // file could be a file object or a JS object
      if (!file && process.env['NODE_ENV'] == 'development') {
        console.error('No file was passed to projectAttachment. Check for logic errors');
        console.trace();
        return;
      }

      this.$fullscreenLoading('Preparing attachment');
      if (file instanceof File) {
        // file object passed
        this.attachmentProjectorFileName = file['name'];
        this.attachmentProjectorType = file['type'];
        this.attachmentProjectorData = URL.createObjectURL(file);
      } else {
        // JS object passed
        this.attachmentProjectorFileName = file['File Name'];
        this.attachmentProjectorType =
          file['MIME Type'] ||
          (await this.$apiCallV2(`/CreditNote/GetMIMEType?filename=${this.attachmentProjectorFileName}`));
        const byteCharacters = atob(
          file['File Data'].startsWith('data:') ? file['File Data'].split(',')[1] : file['File Data'],
        );
        // if File Data is a data URL instead of just base64 data, then remove the prefix, MIME type and base64 indicator before converting to a string
        let untypedByteArray = [];
        for (let index = 0; index < byteCharacters.length; index++) {
          untypedByteArray.push(byteCharacters.charCodeAt(index));
        }

        const blob = new Blob([new Uint8Array(untypedByteArray)], { type: this.attachmentProjectorType });
        this.attachmentProjectorData = URL.createObjectURL(blob);
      }

      this.isProjectable = this.attachmentIsProjectable();
      this.showAttachmentProjector = true;
      this.$fullscreenLoading();
    },
    downloadAttachment: function () {
      const anchor = document.createElement('a');
      anchor['href'] = this.attachmentProjectorData;
      anchor['download'] = this.selectedDoc[0]['File Name'];
      anchor.click();
    },
    closeProjector: function (pointerEvent) {
      this.attachmentProjectorType = ''; // clear MIME Type and base64, because base64 is quite inefficient
      this.attachmentProjectorData = ''; // leaving it there after the projector is closed is quite wasteful
      this.showAttachmentProjector = false;
    },
    addDocument: async function (buttonText) {
      let temp;
      let highestID = 1;
      let document = null;
      switch (buttonText) {
        case 'Add':
          if (!this.documents['docName']) {
            await this.$showAlert('error', 'No Attachment Added', 'Please select an Attachment to proceed');
            return;
          }
          temp = [...this.documentsList];
          this.documentsList = [];

          for (let d of temp) {
            if (d['ID'] > highestID) {
              highestID = d['ID'];
            }
          }

          document = {
            Action: 'N',
            'Doc Remark': !this.documents['remarks'] ? '' : this.documents['remarks'],
            'Doc Date': new Date(),
            'File Name': !this.documents['docName'] ? '' : this.documents['docName'],
            'File Type': !this.documents['filetype'] ? '' : this.documents['filetype'],
            'Doc Image': !this.documents['attachmentFile'] ? '' : this.documents['attachmentFile'],
            'Document GUID': !this.documents['docGuid'] ? '' : this.documents['docGuid'],
            ID: !this.documents['docID'] ? ++highestID : this.documents['docID'],
            _attachmentAction: 'New', // hidden column
          };
          this.$nextTick(() => {
            temp.push(document);
            this.documentsList = temp;
            this.oldDoc = this.documents['docName'];
          });
          this.clearDocumentData();
          this.showAttachmentsPopup = false;
          break;
        case 'Edit':
          temp = [...this.documentsList];
          for (let d of temp) {
            if (d['ID'] > highestID) {
              highestID = d['ID'];
            }
          }
          let existingData = false;
          for (const d of this.selectedDoc) {
            document = {
              Action: 'E',
              'Doc Remark': !this.documents['remarks'] ? '' : this.documents['remarks'],
              'Doc Date': new Date(),
              'File Name': !this.documents['docName'] ? '' : this.documents['docName'],
              'File Type': !this.documents['filetype'] ? '' : this.documents['filetype'],
              'Doc Image': !this.documents['attachmentFile'] ? '' : this.documents['attachmentFile'],
              'Document GUID': !this.documents['docGuid'] ? '' : this.documents['docGuid'],
              ID: !this.documents['docID'] ? ++highestID : this.documents['docID'],
              // "_file": file, // hidden column
            };
            if (d['Document GUID']) {
              existingData = true;
            }
          }
          let temp = this.documentsList;
          const index = temp.findIndex((item) => {
            return existingData ?
                item['Document GUID'] == this.selectedDoc[0]['Document GUID']
              : item['ID'] == this.selectedDoc[0]['ID'];
          });
          temp.splice(index, 1, document);
          this.documentsList = [];
          this.$nextTick(() => {
            this.oldDoc = this.documents['docName'];
            this.documentsList = temp;
            this.clearDocumentData();
          });
          this.showAttachmentsPopup = false;
          break;
        case 'Cancel':
          this.showAttachmentsPopup = false;
          this.clearDocumentData();
          break;
      }
    },
    clearDocumentData: function () {
      this.documents['docName'] = '';
      this.documents['remarks'] = '';
      this.documents['attachmentFile'] = [];
      this.documents['docGuid'] = '';
      this.documents['filetype'] = '';
      this.documents['docID'] = '';
      // this.$refs['docFile'].clearAttachmentClicked();
      this.showDocDetails = false;
      this.docEditIndex = -1;
      this.selectedDoc = [];
    },
    checkPopupEntryFields: function () {
      this.clearErrMessage();
      let error = false;
      if (this.header['creditNoteDate']) {
        const inputDate = new Date(this.header['creditNoteDate']);
        const currentDate = new Date();
        const thresholdDate = new Date();

        thresholdDate.setFullYear(currentDate.getFullYear() - 30);
        if (inputDate <= thresholdDate || inputDate.getFullYear() > currentDate.getFullYear() + 1) {
          this.creditNoteDateErr = 'Credit Note Date is invalid!';
          this.requiredError.push({ key: 'creditNoteDateErr', value: 'Credit Note Date is invalid!' });
          error = true;
        }
      } else {
        this.creditNoteDateErr = 'Credit Note Date is required!';
        this.requiredError.push({ key: 'creditNoteDateErr', value: 'Credit Note Date is required!' });
        error = true;
      }
      if (!this.selectedCreditTypeList) {
        this.creditTypeErr = 'Credit Note Type is required!';
        this.requiredError.push({ key: 'invoicetypeinput', value: 'Credit Note Type is required!' });
        error = true;
      }
      if (!this.headerSelectedCurrency) {
        this.currErr = 'Currency is required!';
        this.requiredError.push({ key: 'currErr', value: 'Currency is required!' });
        error = true;
      }
      if (!this.header['exchRate']) {
        this.exchErr = 'Exchange Rate is required';
        this.requiredError.push({ key: 'exchErr', value: 'Exchange Rate is required!' });
        error = true;
      }
      if (Number(this.header['exchRate']) == 0) {
        this.exchErr = 'Exchange Rate is invalid';
        this.requiredError.push({ key: 'exchErr', value: 'Exchange Rate is invalid!' });
        error = true;
      }
      if (!this.selectedBillCustomer) {
        this.billCustErr = 'Billing Customer is required!';
        this.requiredError.push({ key: 'billCustErr', value: 'Billing Customer is required!' });
        error = true;
      }
      if (this.companyName.length > 40) {
        this.companyNameErr = 'Company Name cannot exceed 40 digits!';
        this.requiredError.push({ key: 'companyNameErr', value: 'Company Name cannot exceed 40 digits!' });
        error = true;
      }
      if (this.header['creditTerm'].length > 4) {
        this.creditTermErr = 'Credit Term cannot exceed 4 digits!';
        this.requiredError.push({ key: 'creditTermErr', value: 'Credit Term cannot exceed 4 digits!' });
        error = true;
      }
      if (!this.header['selectedCreditType'] && this.header['creditTypeStr']) {
        this.creditTypeErr = 'Please select a  valid record!';
        this.requiredError.push({ key: 'creditTypeErr', value: 'Please select a  valid record!' });
        error = true;
      }
      if (this.header['exchRate'].length > 19) {
        this.exchErr = 'Credit Type cannot exceed 19 digits!';
        this.requiredError.push({ key: 'exchErr', value: 'Credit Type cannot exceed 19 digits!' });
        error = true;
      }
      if (this.defaultAddressData.addressLine1.length > 40) {
        this.addressErrors.addressLine1 = 'Address Line 1 cannot exceed 40 characters!';
        this.requiredMessages.push({ Key: 'addressLine1', Value: 'Address Line 1 cannot exceed 40 characters!' });
        error = true;
      }
      if (this.defaultAddressData.addressLine2.length > 40) {
        this.addressErrors.addressLine2 = 'Address Line 2 cannot exceed 40 characters!';
        this.requiredMessages.push({ Key: 'addressLine2', Value: 'Address Line 2 cannot exceed 40 characters!' });
        error = true;
      }
      if (this.defaultAddressData.addressLine3.length > 40) {
        this.addressErrors.addressLine3 = 'Address Line 3 cannot exceed 40 characters!';
        this.requiredMessages.push({ Key: 'addressLine3', Value: 'Address Line 3 cannot exceed 40 characters!' });
        error = true;
      }
      if (this.defaultAddressData.addressLine4.length > 40) {
        this.addressErrors.addressLine4 = 'Address Line 4 cannot exceed 40 characters!';
        this.requiredMessages.push({ Key: 'addressLine4', Value: 'Address Line 4 cannot exceed 40 characters!' });
        error = true;
      }
      if (this.defaultAddressData.city.length > 30) {
        this.addressErrors.city = 'City cannot exceed 30 characters!';
        this.requiredMessages.push({ Key: 'city', Value: 'City cannot exceed 30 characters!' });
        error = true;
      }

      return error;
    },
    requiredClicked: function (item) {
      let errorTabs = this.errorTabMapping.find((e) => e['key'] == item);
      if (errorTabs) {
        if (this.currentTabIndex != errorTabs['tab'] && errorTabs['tab'] >= 0) {
          this.currentTabIndex = errorTabs['tab'];
        }
      }
      this.$nextTick(() => {
        this.$refs[item].setFocus();
      });
    },
    saveClicked: async function () {
      const isInvalid = this.checkPopupEntryFields();

      if (isInvalid) {
        return;
      }

      if (this.newDetailsList.length == 0 && this.settlementList.length > 0) {
        await this.$showAlert(
          'error',
          'Credit Note Details list is empty!',
          'Please key in the Credit Note Details to proceed!',
        );
        return;
      }

      this.openSelectFromDN = false;
      this.openNewSetDet = false;
      this.openNewSet = false;

      let allDocList = this.deletedDocumentsList.concat(this.documentsList);
      let tempAttachment = allDocList.map((item) => {
        return {
          action: item['Action'] ?? this.action,
          doccode: 'ATT',
          docrefno: '',
          remarks: item['Doc Remark'],
          status: '',
          docname: item['File Name'],
          docimage: item['Action'] == 'N' ? item['Doc Image'] : '',
          docimagetype: item['File Type'],
          docguid: this.action == 'N' ? '' : item['Document GUID'], //no guid for new
        };
      });
      let allChargesList = this.newDetailsList.concat(this.deletednewDetailsList);
      let tempChargesList = allChargesList.map((item) => {
        return {
          action: item['Action'] ?? this.action,
          seqno: item['SeqNo'],
          inv_no: item['Inv No'],
          job_no: item['Job No'],
          chargescode: item['Charges Code'],
          tax: item['Tax'],
          amount: item['Credit Amount'] ? item['Credit Amount'].toString() : '',
          famount: item['Credit FAmount'] ? item['Credit FAmount'].toString() : '',
          taxableamt: item['Srv Tax Amount'],
          ftaxableamt: item['Srv Tax FAmount'],
          netamt: item['Net Amount'],
          fnetamt: item['Net FAmount'],
          description: item['Description'] ?? '',
          detail_remark: item['Remark'],
          detail_remark2: item['Remark2'],
          detail_remark3: item['Remark3'],
          consignementno: item['CN #'],
          invtxnid: item['invTxnID'],
          costcode: item['Cost Code'],
          gst2taxcode: item['Tax Code'],
          gst2taxrate: item['Tax'],
          taxable_amountgst2: item['Tax Amt'] ?? '',
          ftaxable_amountgst2: item['FTax Amt'] ?? '',
        };
      });
      let allSettlementList = this.settlementList.concat(this.deletedSettlementList);
      let tempSettlementList = allSettlementList.map((item) => {
        return {
          action: item['Action'] ?? this.action,
          seqno: item['SeqNo'],
          inv_no: item['Inv No'],
          jobno: item['Job No'],
          offset_amount: item['Offset Amount'],
          foffset_amount: item['Offset FAmount'],
          detail_remark: item['Remark'],
          detail_remark2: item['Remark2'],
          detail_remark3: item['Remark3'],
        };
      });

      let getJobNo = this.jobList.length == 1 ? this.jobList[0]['JobNo'] : this.checkSameJob(this.jobList);
      const dataToSave = {
        action: this.action,
        billcu_no: this.selectedBillCustomer?.['Customer Code'] ?? '',
        remarks: this.header['invoiceRem1'] ?? '',
        remarks2: this.header['invoiceRem2'] ?? '',
        remarks3: this.header['invoiceRem3'] ?? '',
        rate: this.header['exchRate'] ?? '',
        currency: this.headerSelectedCurrency?.['Currency Code'] ?? '',
        txntype: '0', // 0-normal cn, 1-temp, 2-contra/adjustment
        reinstate: '0',
        date: new Date(this.header['creditNoteDate']).formatString('yyyy-MM-dd'),
        creditno: this.action == 'N' ? '' : this.header['creditNoteNo'],
        credit_amount: this.totalAmt ?? '0.00',
        fcredit_amount: this.ftotalAmt ?? '0.00',
        credit_balance: this.balanceAmt ?? '0.00',
        fcredit_balance: this.fbalanceAmt ?? '0.00',
        remarks4: this.selectedReasonCode?.['ReasonCode'] ?? '',
        job_no: this.selectedJobInfo?.['JobNo'] ?? getJobNo, //should set from backend
        adjentry: '0',
        reqposting: '0',
        authorized: 'A',
        contrano: '',
        jobconfig: this.selectedCreditTypeList['JobTypeID'],
        updatejobcheck: this.updateJob ? '1' : '0', //need to check
        headerinfocheck:
          this.reportHdrCheck ?
            this.showFromTemplate ? '2'
            : this.showInfoFromJob && this.selectedJobInfo ? '1'
            : '0'
          : '0',
        jobinfoleft: this.showFromTemplate ? this.leftSideText : '',
        jobinforight: this.showFromTemplate ? this.rightSideText : '',
        details: tempChargesList ?? [],
        settlement: tempSettlementList ?? [],

        // region Addtional Info
        additionalinfo: [
          {
            companyname: this.companyName ?? '',
            companytin: this.companyTIN ?? '',
            companyregno: this.companyRegNo ?? '',
            addr1: !this.defaultAddressData['addressLine1'] ? '' : this.defaultAddressData['addressLine1'],
            addr2: !this.defaultAddressData['addressLine2'] ? '' : this.defaultAddressData['addressLine2'],
            addr3: !this.defaultAddressData['addressLine3'] ? '' : this.defaultAddressData['addressLine3'],
            addr4: !this.defaultAddressData['addressLine4'] ? '' : this.defaultAddressData['addressLine4'],
            postcode: !this.defaultAddressData['postcode'] ? '' : this.defaultAddressData['postcode'],
            city: !this.defaultAddressData['city'] ? '' : this.defaultAddressData['city'],
            state: !this.defaultAddressData['state'] ? '' : this.defaultAddressData['state'],
            country: !this.defaultAddressData['country'] ? '' : this.defaultAddressData['country'],
          },
        ],
        headerinfojobno: this.selectedJobInfo?.['JobNo'] ?? getJobNo,
        gweight: this.gweight ?? '',
        measurement: this.measurement ?? '',
        commodity: this.commodity ?? '',

        //endregion

        //region Attachment
        attachment: tempAttachment ?? [],
        //endregion
      };
      this.$fullscreenLoading('Saving Credit Note');
      let result = await this.$apiCallV2('/CreditNote/SaveCreditNote ', dataToSave);
      this.$fullscreenLoading();
      if (result[0]['Results'] != 0) {
        if (this.action == 'N') {
          this.errorMessages.push(`There was an error creating Credit Note: ${result[0]['ErrMsg']}`);
        } else {
          this.errorMessages.push(`There was an error editing Credit Note: ${result[0]['ErrMsg']}`);
        }
        return;
      }

      if ((this.reportHdrCheck && !this.showInfoFromJob && !this.showFromTemplate) || !this.selectedJobInfo) {
        this.selectedHeaderDesc = null;
        this.reportHdrCheck = false;
      }
      this.showInvoiceChargesTable = false;
      this.clearErrMessage();
      this.viewMode = true;
      this.editButtons = [];
      this.$nextTick(async () => {
        await this.populateCreditDetails(this.header['creditNoteNo']);
        this.showInvoiceChargesTable = true;
        await this.refreshClicked();
      });

      if (this.action == 'N') {
        this.successMessages.push(`${result[0]['ErrMsg']}`);
        this.header['creditNoteNo'] = result[0]['ErrMsg'].match(/\(([^)]+)\)/)?.[1];
      } else {
        this.successMessages.push(`${result[0]['ErrMsg']}`);
      }
    },
    checkSameJob: function (list) {
      if (list.length === 0) {
        return '';
      }

      let job = list[0].JobNo;
      let allSame = list.every((obj) => obj.JobNo === job);
      return allSame ? job : '';
    },
    foffsetAmtFocusChanged: function (e) {
      if (!e) {
        this.foffsetAmt = Number(this.foffsetAmt).toFixed(2);
      }
    },
    detailsRateFocusChanged: function (e) {
      if (!e) {
        this.header['exchRate'] = Number(this.header['exchRate']).toFixed(4);
      }
    },
    settleRateFocusChanged: function (e) {
      if (!e) {
        this.header['exchRate'] = Number(this.header['exchRate']).toFixed(4);
      }
    },
    headerRateFocusChanged: async function (e) {
      if (!e) {
        this.header['exchRate'] = Number(this.header['exchRate']).toFixed(4);
        let sameRate = this.header['exchRate'] == this.headerSelectedCurrency['Currency Rate'];
        if (this.action == 'N' && this.newDetailsList.length > 0 && !sameRate) {
          let confirm = await this.$showAlert(
            'warning',
            'Header Exch. Rate Changed',
            `Are you sure you want to change all details Exch. Rate?`,
            [
              { text: 'Yes', color: 'red', textColor: 'white' },
              { text: 'No', color: 'lightgray', textColor: 'black' },
            ],
          );
          if (confirm == 'Yes') {
            //recalculate selling section based on the new rate
            let temp = this.newDetailsList;
            this.newDetailsList = [];
            this.$nextTick(() => {
              this.newDetailsList = temp.map((credit) => {
                credit['Rate'] = Number(this.header['exchRate']).toFixed(4);
                const tempCreditNote = (
                  (Number(credit['Credit FAmount']) * Number(this.header['exchRate'])) /
                  Number(this.headerSelectedCurrency['Factor'])
                ).toFixed(2);
                const taxLocal = (Number(credit['Tax']) / 100) * Number(tempCreditNote);
                const val = (Number(tempCreditNote) + Number(taxLocal)).toFixed(2);
                credit['Credit Amount'] = tempCreditNote.toString();
                credit['Srv Tax Amount'] = Number(taxLocal).toFixed(2);
                credit['Net Amount'] = val.toString();
                return credit;
              });
              //repopulating selected data
              let updatedSelected = this.newDetailsList.find(
                (item) => item['Txnid'] == this.selectedNewDetails?.[0]['Txnid'],
              );
              this.selectedNewDetails = [updatedSelected];
              this.clearChargesDetails();
              this.populateInvData(updatedSelected);
            });
          }
        } else if (this.action == 'E' && this.newDetailsList.length > 0) {
          this.$showAlert(
            'error',
            'Changing Currency Rate is not allowed',
            'Cannot change Currency Rate when Credit Details is not empty!',
          );
          this.$nextTick(() => {
            this.header['exchRate'] = Number(this.oldRate).toFixed(4);
          });
        }
      }
    },
    clearAllData: async function () {
      this.popupTitle = '';
      this.settlementList = [];
      this.newDetailsList = [];
      this.eInvoiceList = [];
      this.deletednewDetailsList = [];
      this.deletedSettlementList = [];
      this.deletedDocumentsList = [];
      this.header['selectedCreditType'] = null;
      this.header['creditTerm'] = '';
      this.createdBy = '';
      this.updatedBy = '';
      this.successMessages = [];
      this.headerSelectedCurrency = null;
      this.header['invoiceDate'] = null;
      this.header['InvoiceNo'] = '';
      this.header['invoiceRem1'] = '';
      this.header['invoiceRem2'] = '';
      this.header['invoiceRem3'] = '';
      this.selectedBillCustomer = null;
      this.selectedCreditTypeList = null;
      this.creditNote['SalesTeam'] = [];
      this.creditNote['Offset'] = [];
      this.selectedSalesTeamList = null;
      this.selectedSalesPersonList = null;
      this.totalDebit = '';
      this.totalCredit = '';
      this.totalInWords = '';
      this.totalAmt = '';
      this.balanceAmt = '';
      this.ftotalAmt = '';
      this.fbalanceAmt = '';
      this.companyName = '';
      this.companyTIN = '';
      this.companyRegNo = '';
      this.defaultAddressData['addressLine1'] = '';
      this.defaultAddressData['addressLine2'] = '';
      this.defaultAddressData['addressLine3'] = '';
      this.defaultAddressData['addressLine4'] = '';
      this.defaultAddressData['postcode'] = '';
      this.defaultAddressData['city'] = '';
      this.defaultAddressData['state'] = '';
      this.defaultAddressData['country'] = '';
      this.documentsList = [];
      this.deletedDocumentsList = [];
      this.selectedDoc = [];
      this.jobList = [];
      this.reportHdrCheck = false;
      this.showInfoFromJob = false;
      this.selectedJobInfo = null;
      this.showFromTemplate = false;
      this.leftSideText = '';
      this.rightSideText = '';
      this.gweight = '';
      this.measurement = '';
      this.commodity = '';
      this.currentTabIndex = 0;
      this.clearErrMessage();
      this.clearChargesDetails();
      this.multiCurr = false;
    },
    clearErrMessage: function () {
      this.creditNoteDateErr = '';
      this.requiredError = [];
      this.errorMessages = [];
      this.creditTypeErr = '';
      this.currErr = '';
      this.billCustErr = '';
      this.companyNameErr = '';
      this.creditTypeErr = '';
      this.creditTermErr = '';
      this.exchErr = '';
      this.deleteInvoiceErr = '';
      this.fromDateErrorMessage = '';
      this.toDateErrorMessage = '';
      this.fromInvoiceListingDateErr = '';
      this.toInvoiceListingDateErr = '';
      this.searchByErrorMessage = '';
      this.searchCriteriaErrorMessage = '';
      this.searchInputErrorMessage = '';
      this.gweightErrMessage = '';
      this.measurementErrMessage = '';
      this.selectedChargesCodeErr = '';
      this.taxErr = '';
      this.gst2taxrateErr = '';
      this.ftaxable_amountgst2Err = '';
      this.fnetamtErr = '';
      this.foffsetAmtErr = '';
      this.settlementDocNoErr = '';
      this.invDNError = '';
    },
    clearChargesDetails: function () {
      this.freeTextJobNo = '';
      this.chargesFreetext = '';
      this.setFOutstanding = '0.00';
      this.setOutstanding = '0.00';
      this.selectedChargesCode = null;
      this.selectedsstTaxCode = null;
      this.selectedSubDetails = [];
      this.selectedInvDN = null;
      this.selectedCostCode = null;
      this.selectedJobno = null;
      this.selectedCNNO = null;
      this.indFOutstanding = '0.00';
      this.indOutstanding = '0.00';
      this.selectedNewDetails = [];
      this.details['famount'] = '0.00';
      this.details['amount'] = '0.00';
      this.details['taxableamt'] = '0.00';
      this.details['tax'] = '0';
      this.selectedTaxCode = null;
      this.details['taxpercent'] = '0';
      this.details['ftaxAmt'] = '0.00';
      this.details['gst2taxrate'] = '0.00';
      this.details['fnetAmount'] = '0.00';
      this.details['detail_remark'] = '';
      this.details['detail_remark2'] = '';
      this.details['detail_remark3'] = '';
      this.documentsList = [];
      this.deletedDocumentsList = [];
      this.selectedDoc = [];
      this.setRem1 = '';
      this.setRem2 = '';
      this.setRem3 = '';
      this.creditExchRate = this.header['exchRate'];
      this.selectedSettlementDocNo = null;
      this.selectedCreditCurrency = this.headerSelectedCurrency;
      this.selectedSettlement = [];
      this.foffsetAmt = '0.00';
    },
    handleFileUpload: async function (file) {
      if (file.length == 0) {
        this.documents['docName'] = '';
        this.documents['filetype'] = '';
        this.documents['attachmentFile'] = [];
      } else {
        // this.$fullscreenLoading('Adding File');c
        this.documents['docName'] = file[0].name;
        this.documents['filetype'] = file[0].name.substring(file[0].name.indexOf('.'));
        let tempAtt = await file[0].toBase64();
        this.documents['attachmentFile'] = tempAtt.substring(tempAtt.indexOf(',') + 1);
        // this.$fullscreenLoading();
      }
    },
    addSettlement: async function (type) {
      switch (type) {
        case 'fromDetails':
          if (this.newDetailsList.length == 0) {
            await this.$showAlert(
              'error',
              'No Credit Note list is empty!',
              'Please key in the credit Note Details first!',
            );
            return;
          }
          break;
        case 'add':
          this.openNewSet = true;
          this.openSelectFromDoc = false;
          this.selectedSettlementDocNo = null;
          this.clearChargesDetails();
          break;
        case 'delete':
          if (this.selectedSettlement.length == 0) {
            await this.$showAlert('error', 'No Settlement Selected', 'Please select a settlement to delete');
            return;
          }
          let confirmCancel = await this.$showAlert(
            'warning',
            'Deleting Settlement Record',
            `Are you sure you want to delete this record ?`,
            [
              { text: 'Yes', color: 'indianred', textColor: 'white' },
              { text: 'No', color: 'lightgray', textColor: 'black' },
            ],
          );
          if (confirmCancel == 'No') return;

          let temp = [];
          if (this.selectedSettlement['0']['Action'] == 'N') {
            temp = this.settlementList.filter((obj) => obj['SeqNo'] != this.selectedSettlement['0']['SeqNo']);
          } else {
            this.settlementList.forEach((obj) => {
              if (obj['SeqNo'] == this.selectedSettlement['0']['SeqNo']) {
                obj['Action'] = 'D';
              }
            });
            let deletedJob = this.settlementList.filter((obj) => obj?.Action == 'D');
            this.deletedSettlementList.push(deletedJob[0]);
            temp = this.settlementList.filter((obj) => obj?.Action != 'D');
          }
          this.settlementList = [];
          let tempDocList = this.fromDocList;
          this.fromDocList = [];
          this.allFromSettlementOffset.forEach((toCheck) => {
            if (
              toCheck['#'] == this.selectedSettlement['0']['#'] &&
              toCheck['Inv No'] == this.selectedSettlement['0']['Inv No']
            ) {
              tempDocList.push(toCheck);
            }
          });
          this.$nextTick(() => {
            this.settlementList = temp;
            this.fromDocList = tempDocList; // update the list
            this.selectedFromDocList = [];
          });
          this.clearChargesDetails();
          break;
      }
    },
    openNewDetails: async function (type) {
      if (type == 'add') {
        if (!this.selectedBillCustomer) {
          await this.$showAlert(
            'warning',
            'No Billing Customer is Selected',
            'Please select Billing Customer to select Invoice Charges from Job Charges',
          );
          return;
        }
        this.invDNList = await this.$apiCallV2(`/CreditNote/GetInvDNNo`, {
          billcu_no: this.selectedBillCustomer['Customer Code'],
          currency: this.headerSelectedCurrency?.['Currency Code'],
          hidefilter: 'Y',
        });
        this.jobnoList = this.jobnoList.filter(
          (item) => item['Billing Customer'] == this.selectedBillCustomer['Customer Code'],
        );
        this.clearChargesDetails();
        this.selectedNewDetails = [];
        this.addEditChargesTitle = 'New Details';
        this.openSelectFromDN = false;
        this.openNewSetDet = true;
        this.$nextTick(() => {
          this.$refs['chargesCode'].setFocus();
        });
      } else {
        if (this.selectedNewDetails.length == 0) {
          await this.$showAlert('error', 'No Detail Selected', 'Please select a detail to delete');
          return;
        }
        let data = this.selectedNewDetails?.[0];
        let confirmCancel = await this.$showAlert(
          'warning',
          'Deleting Credit Note Details Record',
          `Are you sure you want to delete this record ?`,
          [
            { text: 'Yes', color: 'indianred', textColor: 'white' },
            { text: 'No', color: 'lightgray', textColor: 'black' },
          ],
        );
        if (confirmCancel == 'No') return;

        if (data?.['copy']) {
          let tempSub = this.fromInvList;
          this.fromInvList = [];
          let check = this.allFromInvList.filter((item) => item['invTxnID'] == data['invTxnID']);
          tempSub.push(check[0]); // to return the original object
          this.$nextTick(() => {
            this.fromInvList = tempSub;
          });
        }

        let temp = this.newDetailsList;
        this.newDetailsList = [];
        if (this.action == 'N') {
          this.$nextTick(() => {
            this.newDetailsList = temp.filter((obj) => obj['#'] != data['#']); // left side table // new do not have invTxnID
          });
        } else {
          temp.forEach((obj) => {
            if (obj['SeqNo'] == data['SeqNo']) {
              obj['Action'] = 'D';
            }
          });
          let deletedJob = temp.filter((obj) => obj?.Action == 'D');
          this.deletednewDetailsList.push(deletedJob[0]);
          this.$nextTick(() => {
            this.newDetailsList = temp.filter((obj) => obj?.Action != 'D');
          });
        }
        this.clearChargesDetails();
      }
    },
    cancelAddNew: function (type) {
      if (type === 'details') {
        this.openNewSetDet = false;
        this.clearChargesDetails();
        this.clearErrMessage();
      } else if (type === 'settlement') {
        this.openNewSet = false;
        this.clearChargesDetails();
        this.clearErrMessage();
      } else if (type === 'doclist') {
        this.openSelectFromDoc = false;
        this.selectedFromDocList = [];
        this.openNewSet = true;
      } else if (type === 'invlist') {
        this.openSelectFromDN = false;
        this.selectedNewDetails = [];
        this.openNewSetDet = true;
      }
    },
    selectAll: function (type) {
      if (type == 'inv') {
        this.hideSubPackageDetails = false;
        const temp = this.fromInvList;
        this.fromInvList = [];
        this.selectedSubDetails = [];
        temp.forEach((item) => this.selectedSubDetails.push(item));
        this.$nextTick(() => {
          this.fromInvList = temp;
          this.hideSubPackageDetails = true;
        });
      } else {
        this.hideSubPackageSettlement = false;
        const temp = this.fromDocList;
        this.fromDocList = [];
        this.selectedFromDocList = [];
        temp.forEach((item) => this.selectedFromDocList.push(item));
        this.$nextTick(() => {
          this.fromDocList = temp;
          this.hideSubPackageSettlement = true;
        });
      }
    },
    refreshClicked: async function () {
      let errorInvListing = false;
      if (this.filterDateInvList) {
        if (!this.fromInvoiceListingDate) {
          this.fromInvoiceListingDateErr = 'Enter from date!';
          errorInvListing = true;
        }

        if (!this.toInvoiceListingDate) {
          this.toInvoiceListingDateErr = 'Enter to date!';
          errorInvListing = true;
        }
      }

      if (this.filterSearchInvList) {
        if (!this.searchByValue) {
          this.searchByErrorMessage = 'Select search by!';
          errorInvListing = true;
        }

        if (!this.selectedFilterSearchCriteria) {
          this.searchCriteriaErrorMessage = 'Select search criteria!';
          errorInvListing = true;
        }

        if (!this.searchInputValue.trim()) {
          this.searchInputErrorMessage = 'Enter search value!';
          errorInvListing = true;
        }
      }

      if (errorInvListing) {
        return;
      }

      this.mainFilterIsOpen = false;

      const dataToPost = {
        startdate: this.filterDateInvList ? this.fromInvoiceListingDate.formatString('yyyy-MM-dd') : '',
        enddate: this.filterDateInvList ? this.toInvoiceListingDate.formatString('yyyy-MM-dd') : '',
        txntype: '0',
        searchfield: this.filterSearchInvList ? this.searchByValue['key'] : '',
        searchcriteria: this.filterSearchInvList ? this.selectedFilterSearchCriteria['name'] : '',
        searchvalue: this.filterSearchInvList ? this.searchInputValue : '',
      };

      const filterItems = [];
      if (this.filterDateInvList) {
        filterItems.push(
          `date from ${this.fromInvoiceListingDate.formatString('yyyy-MM-dd')} to ${this.toInvoiceListingDate.formatString('yyyy-MM-dd')}`,
        );
      }
      if (this.filterSearchInvList) {
        filterItems.push(
          `${this.searchByValue['Search By']} ${this.selectedFilterSearchCriteria['name']} ${this.searchInputValue}`,
        );
      }
      this.filterText = `Filter by ${filterItems.join(', ')}`;
      this.$fullscreenLoading('Getting Credit Note');
      this.jobTableBoxIsLoading = true;
      this.creditListing = [];
      const getInvoice = this.$apiCallV2('/CreditNote/GetCreditListing', dataToPost).then((result) => {
        this.creditListing =
          this.menu[6]['title'] == 'Show Cancelled Charges' ? result.filter((item) => item.Indicator != 'C') : result;
        this.lastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm');
        this.jobTableBoxIsLoading = false;
        this.$fullscreenLoading();
      }); // start the API call

      return getInvoice;
    },
    headerCurrencySelected: async function (newVal, oldVal) {
      if (this.selectedInvDN) {
        let confirmClear = await this.$showAlert(
          'warning',
          'Changing the Currency will clear the selected Inv./DN details.',
          'Are you sure you want to continue ?',
          [
            { text: 'Yes', color: 'indianred', textColor: 'white' },
            { text: 'No', color: 'lightgray', textColor: 'black' },
          ],
        );
        if (confirmClear == 'No') {
          this.headerSelectedCurrency = oldVal;
          this.currencySelected(oldVal, oldVal['Currency Rate'], false);
          return;
        } else {
          this.invDNList = await this.$apiCallV2(`/CreditNote/GetInvDNNo`, {
            billcu_no: this.selectedBillCustomer?.['Customer Code'],
            currency: this.headerSelectedCurrency?.['Currency Code'],
            hidefilter: 'Y',
          });
          this.clearChargesDetails();
        }
      }

      if (this.newDetailsList.length > 0 && newVal['Currency Code'] != oldVal['Currency Code']) {
        this.$showAlert(
          'error',
          'Changing Currency is not allowed',
          'Cannot change Currency when Credit Details is not empty!',
        );
        this.$nextTick(() => {
          this.headerSelectedCurrency = this.oldCurrency;
          this.currencySelected(this.oldCurrency);
        });
        return;
      } else {
        this.headerSelectedCurrency = newVal;
        this.currencySelected(newVal);
      }
    },
    currencySelected: async function (curr, rate, confirmClear) {
      this.itemCurrencySelected(curr, rate);
      this.header['exchRate'] =
        this.newDetailsList.length > 0 && this.action == 'E' ? Number(this.oldRate).toFixed(4)
        : rate ? Number(rate).toFixed(4)
        : Number(curr['Currency Rate']).toFixed(4);
      this.creditExchRate =
        this.newDetailsList.length > 0 && this.action == 'E' ? Number(this.oldRate).toFixed(4)
        : rate ? Number(rate).toFixed(4)
        : Number(curr['Currency Rate']).toFixed(4);
      this.header['currFactor'] = curr['Factor'];
      this.exchErr = '';
      if (this.selectedBillCustomer && confirmClear) {
        this.selectedInvDN = null;
        this.selectedJobno = null;
        this.selectedSettlementDocNo = null;
        this.invDNList = await this.$apiCallV2(`/CreditNote/GetInvDNNo`, {
          billcu_no:
            this.selectedBillCustomer ? this.selectedBillCustomer['Customer Code'] : this.creditNote[0]['Customer No'],
          currency: curr['Currency Code'],
          hidefilter: 'Y',
        });
        this.settlementDocNoList = await this.$apiCallV2('/CreditNote/GetSettlementDocNo', {
          billcu_no: this.selectedBillCustomer['Customer Code'],
          currency: curr['Currency Code'],
          creditno: this.header['creditNoteNo'].includes('*') ? '' : this.header['creditNoteNo'],
          hidefilter: 'Y',
        });
        this.$nextTick(() => {
          this.selectedCreditCurrency = this.header['currencyList'].getSelected(curr['Currency Code'], 'Currency Code');
        });
      }
    },
    rptHdrChange: function (e) {
      this.selectedHeaderDesc = null;
      this.showInfoFromJob = false;
      this.showFromTemplate = false;
    },
    radioChanged: function (e) {
      this.reportHdrCheck = true;
      switch (e['label']) {
        case 'Show info from Job':
          this.showInfoFromJob = true;
          this.showFromTemplate = false;
          break;
        case 'Show info from template':
          if (this.leftSideText == '' || this.rightSideText == '') {
            this.leftSideText = this.leftSideTextDefault;
            this.rightSideText = this.rightSideTextDefault;
          }
          this.showInfoFromJob = false;
          this.showFromTemplate = true;
          break;
      }
    },
    convertThreeDigit: function (num) {
      // Function to convert a three-digit number to words
      // Array of words representing numbers from 0 to 19
      var words = [
        '',
        'ONE',
        'TWO',
        'THREE',
        'FOUR',
        'FIVE',
        'SIX',
        'SEVEN',
        'EIGHT',
        'NINE',
        'TEN',
        'ELEVEN',
        'TWELVE',
        'THIRTEEN',
        'FOURTEEN',
        'FIFTEEN',
        'SIXTEEN',
        'SEVENTEEN',
        'EIGHTEEN',
        'NINETEEN',
      ];

      // Array of words representing multiples of 10 from 20 to 90
      var tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

      if (num == 0) return '';

      var hundreds = Math.floor(num / 100);
      var remainder = num % 100;

      var result = '';
      if (hundreds > 0) {
        result += words[hundreds] + ' HUNDREDS ';
      }

      if (remainder > 0) {
        if (remainder < 20) {
          result += words[remainder];
        } else {
          var tensDigit = Math.floor(remainder / 10);
          var onesDigit = remainder % 10;
          result += tens[tensDigit];
          if (onesDigit > 0) {
            result += '-' + words[onesDigit];
          }
        }
      }

      return result.trim();
    },
    convertToWords: function (num) {
      // Function to recursively convert a number to words
      // Array of words representing powers of 10 (e.g., thousand, million, billion)
      var scales = ['', 'THOUSAND', 'MILLION', 'BILLION', 'TRILLION', 'QUADRILLION'];
      if (num == 0) return 'ZERO';

      var result = '';
      var scaleIndex = 0;

      while (num > 0) {
        var threeDigits = num % 1000;
        if (threeDigits > 0) {
          result = this.convertThreeDigit(threeDigits) + ' ' + scales[scaleIndex] + ' ' + result;
        }
        num = Math.floor(num / 1000);
        scaleIndex++;
      }

      return result.trim();
    },
    settlementEditButtonClicked: async function (btn, data) {
      if (this.viewMode) return;
      this.clearChargesDetails();
      this.selectedSettlement = [data];
      if (btn == 'Delete') {
        let confirmCancel = await this.$showAlert(
          'warning',
          'Deleting Credit Note Charges Record',
          `Are you sure you want to delete this record ?`,
          [
            { text: 'Yes', color: 'indianred', textColor: 'white' },
            { text: 'No', color: 'lightgray', textColor: 'black' },
          ],
        );
        if (confirmCancel == 'No') return;

        if (this.action == 'N') {
          let temp = this.settlementList;
          this.settlementList = [];

          this.$nextTick(() => {
            if (data['SeqNo']) this.settlementList = temp.filter((obj) => obj['SeqNo'] != data['SeqNo']);
            else this.settlementList = temp.filter((obj) => obj['SeqNo'] != data['SeqNo']);
          });
        } else {
          let temp = this.settlementList;
          this.settlementList = [];
          temp.forEach((obj) => {
            if (obj['SeqNo'] == data['SeqNo']) {
              obj['Action'] = 'D';
            }
          });
          let deletedJob = temp.filter((obj) => obj?.Action == 'D');
          this.deletedSettlementList.push(deletedJob[0]);
          this.$nextTick(() => {
            this.settlementList = temp.filter((obj) => obj?.Action != 'D');
          });
        }

        this.clearChargesDetails();
      } else {
        this.addEditChargesTitle = 'Edit Job Charges';
        await this.populatingSettlementData(data);
        this.openNewSet = true;
        this.openSelectFromDoc = false;
      }
      this.clearErrMessage();
    },
    // Behavior when edit attachment
    attEditButtonClicked: async function (btn, data) {
      await this.projectAttachment({
        // display file retrieved from table
        'File Name': data ? data['File Name'] : '',
        'MIME Type': null,
        'File Data': data ? data['Doc Image'] : '',
      });
    },
    // buttons on details table box clicked
    editButtonClicked: async function (btn, data) {
      if (this.viewMode) return;
      this.clearChargesDetails();
      if (data) {
        this.selectedNewDetails = [data];
        if (btn == 'Delete') {
          let confirmCancel = await this.$showAlert(
            'warning',
            `Deleting Details Record`,
            `Are you sure you want to delete this record ?`,
            [
              { text: 'Yes', color: 'red', textColor: 'white' },
              { text: 'No', color: 'lightgray', textColor: 'black' },
            ],
          );
          if (confirmCancel == 'No') return;

          let tempSub = this.fromInvList;
          this.fromInvList = [];
          if (data['copy']) {
            tempSub.push(data);
          }

          if (this.action == 'N') {
            let temp = this.newDetailsList;
            this.newDetailsList = [];
            this.$nextTick(() => {
              this.fromInvList = tempSub;
              this.newDetailsList = temp.filter((obj) => obj['SeqNo'] != data['SeqNo']);
            });
          } else {
            let temp = this.newDetailsList;
            this.newDetailsList = [];
            temp.forEach((obj) => {
              if (obj['SeqNo'] == data['SeqNo']) {
                obj['Action'] = 'D';
              }
            });
            let deletedJob = temp.filter((obj) => obj?.Action == 'D');
            this.deletednewDetailsList.push(deletedJob[0]);
            this.$nextTick(() => {
              this.fromInvList = tempSub;
              this.newDetailsList = temp.filter((obj) => obj?.Action != 'D');
            });
          }

          this.clearChargesDetails();
        } else {
          this.addEditChargesTitle = 'Edit Details';
          this.openNewSetDet = true;
          this.openSelectFromDN = false;
          await this.populateInvData(data);
        }
        this.clearErrMessage();
      }
    },
    // populating selected inv/dn data
    populateInvData: async function (val) {
      this.$fullscreenLoading('Populating Charges');
      this.chargesDetailsIndex = this.newDetailsList.map((n) => n['#']).indexOf(val['#']);
      this.invDNList = await this.$apiCallV2(`/CreditNote/GetInvDNNo`, {
        billcu_no:
          this.selectedBillCustomer ? this.selectedBillCustomer['Customer Code'] : this.creditNote[0]['Customer No'],
        currency: this.headerSelectedCurrency['Currency Code'],
        hidefilter: 'Y',
      });
      this.selectedChargesCode =
        this.chargesCodeList.length > 0 ?
          this.chargesCodeList.find((item) => item['Charge Code'] == val['Charges Code'])
        : null;
      if (!this.selectedChargesCode) {
        await this.$showAlert('error', 'No record found', 'Charges Code does not exist in the list.');

        this.$nextTick(() => {
          this.$refs['chargesCode'].setFocus();
        });
      }
      if (val['Inv No']) {
        let data = {
          doctype: 'C',
          jobtypeid: this.jobtypeid,
          table: 'CREDITNOTE',
          billcu_no: this.selectedBillCustomer['Customer Code'],
          inv_no: val['Inv No'],
          top: this.rowLimitInputValue,
          jobno: val['Job No'],
        };
        this.jobnoList = await this.getJobList(data);
        // this.invDNList = this.invDNList.filter(item => item['JobNo'] == val['Job No'])
        // this.$nextTick( () => {
        if (val['Job No']) {
          this.selectedJobno = this.jobnoList.find((item) => item['Job No'] == val['Job No']) ?? null;
          this.freeTextJobNo = val['Job No'];
        }
        // })
        this.selectedInvDN = this.invDNList.find((item) => item['Inv No'] == val['Inv No']) ?? null;
      }
      if (this.selectedInvDN) {
        let invFAmt = Number(this.selectedInvDN['F.Total Amount']);
        this.indFOutstanding = invFAmt.toFixed(2) ?? '0.00';
        let invAmt = Number(this.selectedInvDN['Total Amount']);
        this.indOutstanding = invAmt.toFixed(2) ?? '0.00';
      }

      this.selectedCNNO =
        this.consignmentList.length > 0 ?
          this.consignmentList.find((item) => item['Consignment No'] == val['CN #'])
        : null;
      this.selectedCostCode =
        this.costCodeList.length > 0 ? this.costCodeList.find((item) => item['Cost Code'] == val['Cost Code']) : null;
      this.selectedsstTaxCode =
        this.taxCodeList.length > 0 ? this.taxCodeList.find((item) => item['Tax Code'] == val['Tax Code']) : null;
      this.details['detail_remark'] = val['Remark'] ?? '';
      this.details['detail_remark2'] = val['Remark2'] ?? '';
      this.details['detail_remark3'] = val['Remark3'] ?? '';
      this.details['famount'] = val['Credit FAmount'].toString();
      this.details['amount'] = val['Credit Amount'].toString();
      this.selectedCreditCurrency = this.header['currencyList'].getSelected(val['Currency'], 'Currency Code');
      this.creditExchRate = this.selectedCreditCurrency['Currency Rate'].toFixed(4);
      this.fnetamt = val['Net FAmount'].toString();
      this.details['tax'] = val['Tax'].toString();

      // this.ftaxableamt = val['Srv Tax FAmount']; //should be auto computed
      this.$fullscreenLoading();
    },
    // When click on 'edit' button in settlement table, populated settlement details
    populatingSettlementData: async function (val) {
      this.$fullscreenLoading('Populating Settlement');
      this.settlementDocNoList = await this.$apiCallV2('/CreditNote/GetSettlementDocNo', {
        billcu_no: this.selectedBillCustomer['Customer Code'],
        currency: this.headerSelectedCurrency['Currency Code'],
        creditno: this.header['creditNoteNo'],
        hidefilter: 'Y',
      });
      this.selectedSettlementDocNo =
        this.settlementDocNoList.length > 0 ?
          this.settlementDocNoList.find((item) => item['Offset Doc'] == val['Inv No'])
        : null;
      if (this.selectedSettlementDocNo) {
        this.setOutstanding = this.selectedSettlementDocNo['Outstanding'].toString() ?? '0.00';
        this.setFOutstanding = this.selectedSettlementDocNo['F.outstanding'].toString() ?? '0.00';
      }
      // this.offsetAmt = val['Offset Amount'] ? val['Offset Amount'].toString() : '0.00';
      this.foffsetAmt = val['Offset FAmount'] ? val['Offset FAmount'].toString() : '0.00';
      this.setRem1 = val['Remark'];
      this.setRem2 = val['Remark2'];
      this.setRem3 = val['Remark3'];
      this.$fullscreenLoading();
    },
    // Convert amount (numerical) to text
    convertCurrencyToWords: function (cur) {
      // Split currency into whole and fractional parts
      var parts = cur.split('.');
      var wholePart = parseInt(parts[0]);
      var fractionalPart = parseInt(parts[1]) || 0;

      // Convert whole part to words
      var wholeWords = this.convertToWords(wholePart);
      var fractionalWords = this.convertToWords(fractionalPart);

      // Construct the final result
      const currName = this.headerSelectedCurrency?.['Description'] ?? 'MALAYSIAN RINGGIT';
      var result = currName + ' ' + wholeWords;
      if (fractionalPart > 0) {
        result += ' AND ' + fractionalWords + ' CENTS';
      }

      return result + ' ONLY';
    },
    // Calculate total credit amount and balance
    calculateTotalCredit: function () {
      let totalAmt = '';
      let ftotalAmt = '';
      let balanceAmt = '';
      let fbalanceAmt = '';
      totalAmt =
        this.newDetailsList.length == 0 ?
          0
        : this.newDetailsList
            .map((data) => {
              return Number(data['Net Amount']);
            })
            .reduce((a, b) => {
              return a + b;
            });
      ftotalAmt =
        this.newDetailsList.length == 0 ?
          0
        : this.newDetailsList
            .map((data) => {
              return Number(data['Net FAmount']);
            })
            .reduce((a, b) => {
              return a + b;
            });
      balanceAmt =
        this.settlementList.length == 0 ?
          0
        : this.settlementList
            .map((data) => {
              return Number(data['Offset Amount']);
            })
            .reduce((a, b) => {
              return a + b;
            });
      fbalanceAmt =
        this.settlementList.length == 0 ?
          0
        : this.settlementList
            .map((data) => {
              return Number(data['Offset FAmount']);
            })
            .reduce((a, b) => {
              return a + b;
            });
      this.totalAmt = this.$currencyFormat(totalAmt);
      this.ftotalAmt = this.$currencyFormat(ftotalAmt);

      this.balanceAmt = this.$currencyFormat(parseFloat(totalAmt) - parseFloat(balanceAmt));
      this.fbalanceAmt = this.$currencyFormat(parseFloat(ftotalAmt) - parseFloat(fbalanceAmt));
      this.totalInWords = this.convertCurrencyToWords(totalAmt.toString());
    },
    // For summary of events
    getIndicatorName: function (key, value, type) {
      let item = this.summaryItem.find((item) => item.Title === key);

      if (item && item.Indicator) {
        let indicator = item.Indicator.find((indicator) => indicator.code === value);

        if (indicator) {
          return type == 'title' ? indicator.name : indicator.bg;
        }
      }
      return item?.Indicator?.[0]?.name || null;
    },
    // Populate inv/dn details after selecting billing customer
    billCustSelected: async function (newVal, oldVal) {
      if (newVal) {
        if (this.selectedInvDN) {
          let confirmClear = await this.$showAlert(
            'warning',
            'Changing the Billing Customer will clear the selected Inv./DN details.',
            'Are you sure you want to continue ?',
            [
              { text: 'Yes', color: 'indianred', textColor: 'white' },
              { text: 'No', color: 'lightgray', textColor: 'black' },
            ],
          );
          if (confirmClear == 'No') {
            this.selectedBillCustomer = oldVal;
            return;
          } else {
            this.clearChargesDetails();
          }
        }

        this.$fullscreenLoading('Getting Inv./DN No List');
        this.selectedBillCustomer = newVal;
        this.filterDate = true;
        this.header['selectedCreditType'] = this.header['creditTypeList'].find(
          (item) => item['Credit Type'] == newVal['Credit Type'],
        );
        this.header['creditTerm'] = newVal['Credit Term'].toString();
        this.invDNList = await this.$apiCallV2(`/CreditNote/GetInvDNNo`, {
          billcu_no: newVal['Customer Code'],
          currency: this.headerSelectedCurrency?.['Currency Code'],
          hidefilter: 'Y',
        });
        this.settlementDocNoList = await this.$apiCallV2('/CreditNote/GetSettlementDocNo', {
          billcu_no: newVal['Customer Code'],
          currency: this.headerSelectedCurrency?.['Currency Code'],
          creditno: this.header['creditNoteNo'].includes('*') ? '' : this.header['creditNoteNo'],
          hidefilter: 'Y',
        });
        this.internalCustomer = newVal['Internal Customer'];
        this.$fullscreenLoading();
      }
    },
    // behavior when charge code selected
    chargesCodeIsSelected: async function (val) {
      if (val) {
        this.details['tax'] =
          this.selectedBillCustomer['Internal Customer'] == '0' ? val['SST Rate %'].toString() : '0'; // if internal cust not 0 the tax auto zerorise
        this.payableChecking = val['Payable'];
      }
    },
    // when currency (details) is selected
    itemCurrencySelected: function (curr, rate) {
      if (curr) {
        this.header['exchRate'] = rate ? rate.toString() : curr['Currency Rate']?.toString();
        this.creditExchRate = rate ? rate.toString() : curr['Currency Rate']?.toString();
        this.creditFactor = curr['Factor'];
      }
    },
    tabClicked: async function (tabItem, tabIndex) {
      if (this.newDetailsList.length == 0 && this.settlementList.length == 0 && tabIndex == 1) {
        await this.$showAlert('error', 'Credit Note list is empty!', 'Please key in the Credit Note Details first!');
        return;
      } else if (!this.openNewSet && this.selectedChargesCode && tabIndex != 0) {
        const checking = await this.$showAlert(
          'warning',
          `Credit Note not added yet`,
          `Do you wish to discard the changes?`,
          [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
        );
        if (checking === 'Cancel') {
          return;
        }
        this.$nextTick(() => {
          this.clearChargesDetails();
        });
      }

      //auto select the report header description in edit mode.
      this.$nextTick(() => {
        this.currentTabIndex = tabIndex;
        if (this.action == 'E' && tabIndex == 2) {
          if ((this.leftSideText || this.rightSideText) && this.showFromTemplate) {
            this.selectedHeaderDesc = this.headerDescRadioButtons[1];
            this.reportHdrCheck = true;
            this.showFromTemplate = true;
            this.showInfoFromJob = false;
          } else if (this.selectedJobInfo || this.selectedHeaderDesc?.['keyValue'] == '1') {
            this.selectedHeaderDesc = this.headerDescRadioButtons[0];
            this.reportHdrCheck = true;
            this.showInfoFromJob = true;
            this.showFromTemplate = false;
          } else {
            this.leftSideText = this.leftSideTextDefault;
            this.rightSideText = this.rightSideTextDefault;
            this.showInfoFromJob = false;
            this.showFromTemplate = false;
          }
        }
      });
    },
    // Retrieve job list
    getJobList: async function (data) {
      let getJobList = await this.$apiCallV2(`/CreditNote/GetQueryByJobConfig`, data);

      return getJobList;
    },
    // Populate credit charges after selecting inv/dn
    getCreditCharges: async function (val) {
      // this.$fullscreenLoading('Getting Inv/DN list');
      let dataToPost = {
        date: this.openNewSetDet ? val['Inv Date'].split(' ')[0] : val['DateUpdated'].split(' ')[0],
        inv_no: val['Inv No'],
      };
      let temp = await this.$apiCallV2('/CreditNote/GetCreditCharges', dataToPost);
      this.allFromInvList = temp;
      // this.$fullscreenLoading();
      let tempAll = this.newDetailsList.concat(this.deletednewDetailsList);
      temp.forEach((item, index) => {
        item['#'] = tempAll.length + index + 1;
      });
      this.newDetailsList.forEach((toCheck) => {
        temp = temp.filter((item) => item['#'] != toCheck['#']);
      });
      this.$nextTick(() => {
        this.fromInvList = [];
        this.fromInvList = temp;
      });
    },
    // Behaviour after selecting inv/dn
    invDNIsSelected: async function (val) {
      if (val) {
        this.freeTextJobNo = '';
        this.selectedJobno = null;
        this.getCreditCharges(val);
        if (!this.openSelectFromDN) {
          this.validateJob(val);
        }
      }
    },
    // Initialise amounts after selecting settlement doc no
    settlementDocNoIsSelected: async function (val) {
      if (val) {
        this.setFOutstanding = val['F.outstanding'].toFixed(2);
        this.setOutstanding = val['Outstanding'].toFixed(2);
        let reformat = Number(this.fbalanceAmt.replaceAll(',', ''));
        this.foffsetAmt = Math.min(val['F.outstanding'], Number(reformat)).toFixed(2);
      }
    },
    // Tax price loss focus
    taxPriceFocusOut: function (state, currentData) {
      //function not use, switch to taxCodeSelected
      if (!state) {
        if (currentData == 'myrEquivalentCredit') {
          //not use in credit note, is a copy over from ForeignCurrency component
          let temp = Number(this.netInclTax) - Number(this.srvTaxAmt);
          this.sellingTotalPrice = (Number(temp) / Number(this.header['exchRate'])) * Number(this.creditFactor);
        }

        if (currentData == 'selectedsstTaxCode') {
          if (this.selectedsstTaxCode['Tax Type'] == 'GST') {
            //what is this for?
            this.$showAlert(
              'success',
              'Deleted!',
              `Credit Note ${this.selectedCreditListing[0]['Credit No']} was successfully deleted.`,
            );
          }
          this.details['tax'] =
            this.selectedsstTaxCode ?
              this.selectedsstTaxCode['RATE'].toString()
            : this.selectedChargesCode['SST Rate %'].toString();
        }
      }
    },
    taxCodeSelected: function (t) {
      if (t) {
        //selected tax code
        this.details['tax'] = t['RATE'].toString();
      } else {
        this.details['tax'] = this.selectedChargesCode ? this.selectedChargesCode['SST Rate %'].toString() : '';
      }
    },
    // Check if job belongs to selected biling customer
    validateJob: async function (val) {
      if (val) {
        this.jobnoList = [];
        this.freeTextJobNo = '';
        if (val['JobNo']) {
          // get the job details
          let data = {
            doctype: 'C',
            jobtypeid: this.jobtypeid,
            table: 'CREDITNOTE',
            billcu_no: this.selectedBillCustomer['Customer Code'],
            inv_no: val['Inv No'],
            top: this.rowLimitInputValue,
            jobno: val['JobNo'],
          };
          this.jobnoList = await this.getJobList(data);
          this.selectedJobno = this.jobnoList.getSelected(val['JobNo'], 'Job No') ?? null;
          this.freeTextJobNo = val['JobNo'];
          if (this.jobnoList.length == 0) {
            // get the job billing cust

            let data = {
              doctype: 'C',
              jobtypeid: this.jobtypeid,
              table: '',
              billcu_no: this.selectedBillCustomer['Customer Code'],
              inv_no: val['Inv No'],
              top: this.rowLimitInputValue,
              jobno: val['JobNo'],
            };
            let checkJob = await this.getJobList(data);
            if (this.selectedBillCustomer['Customer Code'] != checkJob[0]['Billing Customer'] && this.action != '') {
              let confirmCancel = await this.$showAlert(
                'warning',
                `Job belong to ${checkJob[0]['Billing Customer']}`,
                `Do you still want to continue?`,
                [
                  { text: 'Yes', color: 'indianred', textColor: 'white' },
                  { text: 'No', color: 'lightgray', textColor: 'black' },
                ],
              );
              if (confirmCancel == 'No') {
                this.selectedJobno = null;
                this.freeTextJobNo = '';
              }
            }
          }
        }

        this.$nextTick(() => {
          this.selectedInvDN = this.invDNList.find((item) => item['Inv No'] == val['Inv No']);
          let local = Number(this.selectedInvDN['Total Amount']).toFixed(2);
          let foreign = Number(this.selectedInvDN['F.Total Amount']).toFixed(2);
          this.indFOutstanding = foreign.toString();
          this.indOutstanding = local.toString();
          let check =
            this.selectedNewDetails.length > 0 ?
              this.selectedNewDetails?.[0]['Inv No'] === this.selectedInvDN?.['Inv No']
            : true;
          // if(!check){
          this.details['famount'] = (
            Number(this.selectedInvDN['F.Total Amount']) - Number(this.selectedInvDN['Credited Amount'])
          ).toFixed(2);
          // }
        });
      }
    },
    // Click print in popup entry
    popupPrintClicked: function () {
      this.openPrintCNPage(this.header.creditNoteNo);
    },
    // Open Print CN in new window
    openPrintCNPage: function (cnno) {
      let printCreditNoteRoute;
      if (cnno) {
        printCreditNoteRoute = this.$router.resolve(
          `/Home/${this.$route.params.user}/FINAR/Print Credit Note?cnno=${cnno.trim?.() ?? ''}`,
        );
      } else {
        printCreditNoteRoute = this.$router.resolve(`/Home/${this.$route.params.user}/FINAR/Print Credit Note`);
      }
      window.open(printCreditNoteRoute.href, '_blank'); // open in a new tab
    },
    async sasControlFunction() {
      this.$fullscreenLoading('Checking Security Access');
      const sasControl = await this.$apiCallV2('/SASControl/GetSASControlAccess?system=FINAR&page=Credit Note');

      this.menu.forEach((m) => {
        m.enabled = true;
        m.oriEnabled = true; //have this oriEnabled because some function will need to enable or disable the menu so have to use another key so that it wont enable any menu button that is suppose to be disable
      });

      sasControl.forEach((k) => {
        //function check
        if (k.Control == 'txtTaxRate_1' && k.Properties === 'Enabled') {
          this.SASEnableTaxRate = k.PropertiesValue === 'True';
          return; //go to the next iteration
        }
        //key in menu same as control value from api
        const selectedMenu = this.menu.filter((m) => m['key'] == k['Control']); //special case uses filter because got more than 1 menu uses same key
        if (selectedMenu.length > 0) {
          selectedMenu.forEach((selectedMenu) => {
            selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] == 'True';
            selectedMenu['oriEnabled'] = selectedMenu['enabled'];
          });
        }
      });

      this.$fullscreenLoading();
    },
  },
  async mounted() {
    await this.sasControlFunction();

    this.mountedPromises.push(
      this.$apiCallV2('/Login/GetDetails').then((result) => {
        this.userid = result.LoginUserId;
        this.mountedPromisesFulfilledCount++;
      }),
    );
    this.mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'currency' }).then((result) => {
        this.header['currencyList'] = result;
        this.mountedPromisesFulfilledCount++;
      }),
    );

    this.mountedPromises.push(
      this.$apiCallV2('/CreditNote/GetJobTypeConfig', { doctype: 'C' }).then((result) => {
        this.header['creditTypeList'] = result;
        this.mountedPromisesFulfilledCount++;
      }),
    );
    this.mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CHARGESCODE', module: 'FINAR' }).then((result) => {
        this.chargesCodeList = result;
        this.mountedPromisesFulfilledCount++;
      }),
    );
    this.mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'COSTCODE' }).then((result) => {
        this.costCodeList = result;
        this.mountedPromisesFulfilledCount++;
      }),
    );
    this.mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'COMPANY' }).then((result) => {
        this.companyList = result;
        this.mountedPromisesFulfilledCount++;
      }),
    );
    this.mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'postcode' }).then((result) => {
        this.postcodeList = result;
        this.mountedPromisesFulfilledCount++;
      }),
    );
    this.mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'state' }).then((result) => {
        this.stateList = result;
        this.mountedPromisesFulfilledCount++;
      }),
    );
    this.mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'country' }).then((result) => {
        this.countryList = result;
        this.mountedPromisesFulfilledCount++;
      }),
    );
    this.mountedPromises.push(
      this.$apiCallV2('/CreditNote/GetCreditReason', { reasoncode: '' }).then((result) => {
        this.reasonCodeList = result;
        this.mountedPromisesFulfilledCount++;
      }),
    );
    this.mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'ARTAXCODE', module: '' }).then((result) => {
        let tempTax = result;
        this.taxCodeList = this.GSTEnabled ? tempTax : tempTax.filter((tax) => tax['Tax Type'] == 'SST');
        this.mountedPromisesFulfilledCount++;
      }),
    );
    this.mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SALESTEAM', module: 'FINAR' }).then((result) => {
        this.salesTeamList = result;
        this.mountedPromisesFulfilledCount++;
      }),
    );
    this.mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SALESPERSON', module: 'FINAR' }).then((result) => {
        this.salesPersonList = result;
        this.mountedPromisesFulfilledCount++;
      }),
    );

    this.$fullscreenLoading('Setting up Sizing Observer');
    this.resizeObserver = new ResizeObserver(this.leftContainerResized);
    this.resizeObserver.observe(this.$refs['leftContainer']);
    this.$fullscreenLoading();

    await Promise.allSettled(this.mountedPromises);
    this.refreshClicked();
    this.menu = this.menu.map((menu) => {
      menu.enabled == true;
      return menu;
    });

    if (this.$route.query['invno']) {
      this.filterSearchInvList = true;
      this.filterDateInvList = false;
      this.searchByValue = this.searchFilterItems.getSelected('Credit No', 'Search By');
      this.selectedFilterSearchCriteria = this.searchCriteriaItems.getSelected('EQUALS', 'name');
      this.searchInputValue = this.$route.query['invno'];
      await this.refreshClicked();
      this.selectedCreditListing = this.creditListing.filter((i) => i['Credit No'] === this.$route.query['invno']);
      // SAS Control
      const menuTitle = this.$route.query['action'] == 'E' ? 'Edit Credit Note' : 'View Credit Note';
      const selectedMenu = this.menu.getSelected(menuTitle, 'title');
      if (!selectedMenu['enabled']) {
        this.$showAlert('warning', 'Access Denied', `You do not have permission to ${menuTitle}`);
        return;
      }
      this.$nextTick(() => {
        this.menuClicked({
          title: menuTitle,
        });
      });
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  watch: {
    selectedCreditListing: function (val) {
      if (val.length > 0) {
        if (val[0]['Indicator'] == 'C') {
          this.menu[5]['enabled'] = this.menu[5]['oriEnabled'] ? true : false;
        } else {
          this.menu[5]['enabled'] = false;
        }
      } else {
        this.menu[5]['enabled'] = this.menu[5]['oriEnabled'] ? true : false;
      }
    },
    selectedDoc: function (val) {
      if (val) {
        this.documents['remarks'] = val['Doc Remark'];
      }
    },
    selectedNewDetails: async function (val) {
      if (val.length > 0 && this.action == '') {
        //will auto populate in view mode only
        this.openNewSetDet = true;
        this.openSelectFromDN = false;
        await this.populateInvData(val[0]);
      }
    },
    selectedSettlement: async function (val) {
      if (val.length > 0 && this.action == '') {
        //will auto populate in view mode only
        this.openNewSet = true;
        this.openSelectFromDoc = false;
        await this.populatingSettlementData(val[0]);
      }
    },
    headerSelectedCurrency: function (val) {
      if (!val) {
        this.header['exchRate'] = '';
        this.exchErr = '';
      } else {
        if (
          this.newDetailsList.length > 0 &&
          this.action == 'E' &&
          val['Currency Code'] != this.oldCurrency['Currency Code']
        ) {
          this.$showAlert(
            'error',
            `You are not allowed to change the currency`,
            `Currency will remain as ${this.oldCurrency['Currency Code']}`,
          );
          this.$nextTick(() => {
            this.headerSelectedCurrency = this.oldCurrency;
            this.currencySelected(this.oldCurrency);
          });
        }
      }
    },
    settlementList: function (val) {
      if (val) {
        this.calculateTotalCredit();
      }
    },
    newDetailsList: {
      deep: true,
      handler(val) {
        if (val) {
          this.jobList = [];
          this.calculateTotalCredit();
          this.multiCurr = !val.every((d) => d['Currency'] == this.headerSelectedCurrency['Currency Code']);
          let tempnewDetailsList = val.filter((job) => job['Job No'].trim() != ''); //filter empty job
          let current = '';
          tempnewDetailsList.forEach((job) => {
            if (current != job['Job No'].trim()) {
              //filter duplicated job
              current = job['Job No'];
              this.jobList.push({ JobNo: job['Job No'] });
            }
          });
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.creditnote-main {
  display: flex;
  font-size: 12px;
  padding: 10px 10px 10px 45px;

  > .left-container {
    display: flex;
    justify-content: center;

    > .loading-bar-container {
      border: 1px solid rgba(0, 0, 0, 0.2);
      flex-grow: 1;
      height: 15px;
      position: absolute;
      bottom: 15px;
      z-index: 101;

      > .loading-bar-text {
        width: 100%;
        height: 100%;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
      }

      > .loading-bar {
        height: 100%;
        background-color: rgb(60, 211, 0);
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 0;
        z-index: 9;
      }
    }
  }

  > .right-container {
    width: 100%;
    margin-left: 5px;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    > .top-section,
    > .bottom-section {
      background-color: white;
      border-radius: 5px;
      padding: 5px;
    }

    > .top-section {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      > .filter-description {
        padding-right: 8px;
      }

      > .button-filter-component {
        margin-right: 5px;
        position: relative;

        .filter-popup {
          display: flex;
          flex-direction: column;

          > button {
            margin-top: 5px;
            width: fit-content;
            align-self: flex-end;
          }
        }
      }
    }

    > .bottom-section {
      height: calc(100% - 38px - 5px); // deduct height of top-section and margin
      margin-top: 5px;
    }
  }

  > .attachments-popup-container {
    > .attachments-popup {
      ::v-deep {
        // deep needed to target the content class inside the PopupV2 component
        .content {
          // NOT the content class for slot, but content class inside PopupV2 itself
          overflow: visible;
        }
      }

      .attachments-popup-content {
        overflow: visible;

        > .attached-file {
          text-align: left;
          margin-bottom: 8px;

          > .file-link {
            cursor: pointer;
            user-select: none;

            &:hover {
              text-decoration: underline;
            }

            &:active {
              background-color: black;
              color: white;
            }
          }
        }
      }
    }
  }

  > .attachment-projector {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 99999;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;

    > .attachment-projection {
      height: 95%;
      width: 80%;

      > object {
        height: 100%;
        width: 100%;
        object-fit: scale-down;
      }

      > .attachment-not-projectable-container {
        height: 100%;
        width: 100%;
        background-color: rgba(0, 0, 0, 0.7);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        font-size: 40px;

        > span {
          margin: 10px 0 10px 0;
          color: white;
        }

        > button {
          height: 50px;
          width: 150px;
          margin: 10px 0 10px 0;
          font-size: 16px;

          > img {
            height: 24px;
            width: 24px;
          }
        }
      }
    }

    > .close-button {
      height: 50px;
      width: 50px;
      position: absolute;
      top: 10px;
      right: 10px;
      border-radius: 100px;
      background-color: white;
      text-align: center;
      font-size: xx-large;
      cursor: pointer;
      user-select: none;
    }
  }
}

.summary-row {
  display: flex;
  flex-direction: row;
  width: fit-content;

  > :nth-child(1) {
    margin-right: 5px;
  }
}
.cn-entryfields {
  height: calc(100% - 35px);
  width: 100%;
  overflow: scroll;
}

.first-section {
  display: flex;
  width: 100%;

  > .left-section {
    display: flex;
    flex-direction: column;
    width: 20%;
    margin-right: 10px;
  }

  > .right-section {
    display: flex;
    flex-direction: column;
    width: 80%;

    > .right-section_top {
      display: flex;
      flex-direction: row;
    }

    > .right-section-bottom {
      width: 100%;

      > .box-contatiner-section {
        display: flex;
        flex-direction: row;
        width: 100%;
      }
    }
  }
}
.summary-block {
  position: relative;
  margin-bottom: 2px;
  margin-right: 3px;

  > .summary-item {
    border: 1px solid rgba(128, 128, 128, 0.5);
    max-width: 120px;
    min-width: 120px;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-box-orient: vertical;
    cursor: default;
    padding: 3px;
  }
  > .summary-item-printer {
    border: 1px solid rgba(128, 128, 128, 0.5);
    max-width: 100px;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-box-orient: vertical;
    cursor: default;
    padding: 3px;
  }

  > .milestone-tooltip {
    position: absolute;
    text-overflow: wrap;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
  }
}

.legend-items {
  display: flex;
  align-items: center;
  margin-right: 3px;

  > .legend-box {
    height: 10px;
    width: 10px;
    border: 1px solid rgba(128, 128, 128, 0.5);
    margin-right: 3px;
  }
}
.table-container {
  border: 1px solid rgba(0, 0, 0, 0.5);
  padding: 5px;
  margin-top: 3px;
  height: 100%;

  > .tabulator-title {
    width: 100%;
    display: flex;
    justify-content: end;
    align-items: center;
    margin-bottom: 5px;

    > button {
      margin-left: 3px;
    }
  }
}
.totalAmt-block {
  border: 1px solid rgb(0, 0, 0);
  display: flex;
  flex-direction: row;
  width: 300px;
  height: fit-content;

  > .left-block {
    width: 150px;
    text-align: right;
    padding-right: 6px;
  }

  > .right-block {
    width: 150px;
    background-color: #467cdc;
    color: white;
    text-align: right;
    padding-right: 6px;
  }
}
.total-block {
  border: 1px solid rgba(128, 128, 128, 0.5);
  max-width: 210px;
  min-width: 210px;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: default;
  padding: 3px;
}
> .button-filter-component {
  margin-right: 5px;
  position: relative;

  .filter-popup {
    display: flex;
    flex-direction: column;

    > button {
      margin-top: 5px;
      width: fit-content;
      align-self: flex-end;
    }
  }
}
.foreign-currency-main {
  font-size: 12px;
  padding: 10px;
  border-radius: 10px;
  position: relative;
  background-color: rgb(225, 255, 255);
}
.foreign-currency-background {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  z-index: -1;

  > .background-left {
    // flex: 1;
    width: calc(33.33% + 4px);
    // background-color: rgb(225, 255, 255);
    border-right: 3px solid white;
  }

  > .background-middle {
    flex: 1;
    // background-color: transparent;
    border-left: 3px solid white;
    border-right: 3px solid white;
  }

  > .background-right {
    // flex: 1;
    width: calc(33.33% + 4px);
    // background-color: rgb(225, 255, 255);
    border-left: 3px solid white;
  }
}
.foreign-currency-style {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 30px;
  flex-direction: row;
}

.foreign-currency-row {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 20px;
  flex-direction: row;

  > :not(:first-child) {
    margin-left: 5px;
  }

  > .foreign-currency-column {
    flex: 1;
  }
}

.api-loading {
  position: absolute;
  left: 0;
  top: 0;
  height: 90%;
  width: 50%;
  background-color: rgba(100, 100, 100, 0.7);
  z-index: 10;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  &::before {
    content: 'Loading ' attr(name);
  }
}
</style>
